/*
Description: This is the file that will handle interacting with the WebPT EMR
Date: 12/6/2023
Author: Jacob Michalski

Notes:
    All Injectors need the following functions:
        getQuestions
        answerQuestions (Consider combining these and making the single a private function)
        answerQuestion

WepPT Specific Notes:
    Looking at an Initial Evaluation, we have the following sections:
        ..
        Get the Tabs with id="tabs"
            var tabs = document.querySelector("#tabs")
            # Only look at the ones that have children, the others are ghosts
                Once you have one of the tabs, you need to get the actionable elements - the questions
                    input
                    select
                    H3's with a bunch of input's following it
                    H4's are subheadings within an H3

                    # watch out for currently hidden elements... they will potentially show once you select an option
                    thistab.querySelectorAll("select")[7].checkVisibility()
*/

/*
Note Capture ("get all notes") state.

The capture poller reuses the panel scrape to snapshot the open note in the
background every few seconds. That scrape is written for a user-triggered
Comprehend, so it may nudge the DOM to reveal a field and it uploads the whole
page on error. Neither is acceptable on a timer, so the capture entry points
below raise this flag and the affected sites opt out while it is set.
*/
let captureScrapeActive = false;

// getQuestions' Objective key space depends on this account preference, which
// only the panel passes in. Remember it so capture builds the same keys the
// panel does. Latch rather than assign: some callers (MinimalFloatingUI) invoke
// getQuestions() with no argument, and re-reading that as "false" would flip the
// key space back and forth, accumulating both spaces for one note in the store.
let lastKnownIsWebPTUser = false;

// Multi-choice fields (checkbox groups, multi-selects) -> their options. Kept out
// of the field object so legacy flex payloads are unchanged; NewWebPTInject
// reads it to emit native flex++ multi-select leaves.
export const multiChoiceFields = new WeakMap<object, string[]>();
function markMultiChoice<T extends object>(field: T, choices: string[]): T {
  multiChoiceFields.set(field, choices);
  return field;
}

export function getPageInfo() {
  // function getPageInfo() {
  /*
    Description: This function will get the page info from the note
    Return: A dictionary with the page info
        {
            "URL": Base URL for the page,
            "Note Type": "PT Daily/SOAP Note",...
        }
    */
  const baseURL = window.location.origin;
  const noteTypeElement = document.querySelector('#noteTitle');
  let noteType = noteTypeElement?.textContent.trim() || 'WebPT Note';

  try {
    noteType = noteTypeElement.innerHTML
      .replace(/<div[^>]*>[\s\S]*?<\/div>/gi, '')
      .trim();
  } catch (error) {
    console.error('Error: ' + error);
    getLogFromInjector();
  }

  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

// The poller reads page info before the questions, and getPageInfo's catch
// block calls getLogFromInjector, so the flag has to cover this call too.
export function getPageInfoForCapture() {
  captureScrapeActive = true;
  try {
    return getPageInfo();
  } finally {
    captureScrapeActive = false;
  }
}

export function getPatientInfo() {
  /*
    Description: This function will get the patient info from the note
    Return: A dictionary with the patient info
    {
        "EMR ID": 0x1234 or "michalskij"
        "First Name":firstName,
        "Last Name": lastName,
        "DOB": dob,
    }
    */
  // name=ctl00_cp1_patientFirst
  // use the query selector to get the patient info
  let patientInfoDiv = document.querySelector('.patientNameInfo')?.innerText;
  // Remove Patient: and (EMR ID: 1234)
  patientInfoDiv = patientInfoDiv?.replace('Patient: ', '');
  patientInfoDiv = patientInfoDiv?.replace(/\(.*\)/, '');
  patientInfoDiv = patientInfoDiv?.trim();

  const firstName = patientInfoDiv?.split(',', 2)[1].trim();
  const lastName = patientInfoDiv?.split(',', 2)[0].trim();
  const dob = getDOB();
  // Get the element by its id
  let emrID =
    document.getElementById('ID')?.value.trim() ||
    firstName?.toLowerCase() + lastName?.toLowerCase() + 'WebPT';

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

// Capture runs unattended, so it must not throw: getPatientInfo assumes the
// header text holds "Last, First", and a throw here would kill the whole tick.
export function getPatientInfoForCapture() {
  try {
    if (!document.querySelector('.patientNameInfo')) return null;
    return getPatientInfo();
  } catch (_) {
    return null;
  }
}

export async function getSchedule() {
  const date = getCurrentScheduleDate();
  return getScheduleFromSchedulerApi(date);
}

function fetchWebPTScheduleViaBackground(payload) {
  // Use a dedicated port instead of sendMessage: a one-shot sendMessage is
  // broadcast to every extension context (incl. the open side panel), and one of
  // them resolves the promise empty before the background's async reply lands.
  return new Promise((resolve, reject) => {
    const port = chrome.runtime.connect({ name: 'webpt-schedule' });
    let settled = false;
    port.onMessage.addListener((response) => {
      settled = true;
      resolve(response);
      port.disconnect();
    });
    port.onDisconnect.addListener(() => {
      if (!settled) reject(new Error('WebPT schedule connection closed'));
    });
    port.postMessage(payload);
  });
}

async function getScheduleFromSchedulerApi(date) {
  const facilityId = await getWebPTFacilityId();
  if (!facilityId) throw new Error('Unable to determine WebPT facility.');

  // The scheduler API only exists on app.webpt.com. On emr.webpt.com (WebPT 2.0)
  // the content script can't reach it cross-origin, so the background service
  // worker performs the fetch (over a port) with the user's WebPT session cookies.
  const result = await fetchWebPTScheduleViaBackground({
    date,
    nextDate: getNextScheduleDate(date),
    facilityId: String(facilityId)
  });
  console.log(
    '[CPT sync] background fetch status=',
    result?.status,
    '| ok=',
    result?.ok
  );

  if (!result?.ok) {
    throw new Error(
      `Unable to fetch WebPT schedule: ${result?.status ?? result?.error ?? 'no response'}`
    );
  }

  // A stale WebPT session returns the login HTML with a 200, so guard the parse.
  let schedule;
  try {
    schedule = JSON.parse(result.body);
  } catch {
    schedule = null;
  }
  if (!schedule) {
    throw new Error(
      'Could not read your WebPT schedule — your WebPT session may have expired. Log in to WebPT and try Sync Cal again.'
    );
  }

  const ownCalendarId = getOwnCalendarId(schedule);
  const events = (schedule.events || []).filter(
    (event) => ownCalendarId == null || String(event.c_id) === ownCalendarId
  );

  const appointments = events
    .map((event) => mapWebPTScheduleEvent(event, schedule, date))
    .filter(Boolean);

  return { appointments, date };
}

// WebPT scheduler status codes: 1 = scheduled, 3 = checked-in, 5 = cancelled.
const WEBPT_CANCELLED_STATUS = 5;

// Resolve the logged-in user's scheduler calendar id (c_id) by matching the
// user's name against the schedule's cal_names. The userId is not the c_id, so
// the name is the only available join — but both strings come from WebPT, so the
// match is reliable in practice. Returns null when no confident match is found,
// in which case the caller keeps every appointment rather than risk dropping the
// user's own visits.
function getOwnCalendarId(schedule) {
  const fullName = document
    .querySelector('.user-dropdown .menu-panel-arrow')
    ?.textContent?.trim();
  const calNames = schedule?.cal_names;
  if (!fullName || !calNames) return null;

  const target = normalizeProviderName(fullName);
  if (!target) return null;

  const match = Object.entries(calNames).find(
    ([, name]) => normalizeProviderName(name) === target
  );
  return match ? match[0] : null;
}

// Lowercase, drop accents/punctuation, and sort name tokens so "First Last"
// and "Last, First" compare equal. Names with extra tokens (e.g. appended
// credentials) won't match and fall back to no filtering.
function normalizeProviderName(name) {
  return (
    String(name || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .match(/[a-z]+/g)
      ?.sort()
      .join(' ') || ''
  );
}

function mapWebPTScheduleEvent(event, schedule, date) {
  const appointmentStart = getScheduleEventStart(event.start_date);
  const appointmentDate = getScheduleEventDate(appointmentStart);
  if (!appointmentDate || appointmentDate !== date) return null;

  const parsedPatient = parseCalendarPatientText(event.title);
  if (!parsedPatient.firstName || !parsedPatient.lastName) return null;

  return {
    appointmentId: event.id != null ? String(event.id) : undefined,
    firstName: parsedPatient.firstName,
    lastName: parsedPatient.lastName,
    dob: parsedPatient.dob,
    date: appointmentDate,
    scheduledAt: getScheduleEventTimestamp(appointmentStart),
    noteType: event.apt_type_legacy_name || undefined,
    provider: schedule.cal_names?.[String(event.c_id)] || undefined,
    status:
      Number(event.status) === WEBPT_CANCELLED_STATUS
        ? 'cancelled'
        : event.status != null
          ? String(event.status)
          : undefined,
    emrId: event.p_id != null ? String(event.p_id) : undefined
  };
}

async function getWebPTFacilityId() {
  // WebPT 1.0 exposes the facility as a page global (window.intFacilityID).
  // WebPT 2.0 (emr.webpt.com) doesn't, but carries facility_id in the page URL.
  // (The auth-token cookie is httpOnly, so it's unreadable from JS.)
  const fromPage = await getWebPTFacilityIdFromMainWorld();
  const fromUrl = getWebPTFacilityIdFromUrl();
  console.log(
    '[CPT facility] href=',
    window.location.href,
    '| fromPage=',
    fromPage,
    '| fromUrl=',
    fromUrl
  );
  return fromPage || fromUrl || undefined;
}

function getWebPTFacilityIdFromUrl() {
  const match = window.location.href.match(/[?&#]facility_id=(\d+)/);
  return match ? match[1] : undefined;
}

function getWebPTFacilityIdFromMainWorld() {
  const reqId = `webpt_facility_${Date.now()}`;
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(undefined);
    }, 500);

    const handler = (event: MessageEvent) => {
      if (
        event.source === window &&
        event.data?.action === 'getWebPTFacilityId_response' &&
        event.data.reqId === reqId
      ) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', handler);
        resolve(event.data.result || undefined);
      }
    };

    window.addEventListener('message', handler);
    window.postMessage({ action: 'getWebPTFacilityId', reqId }, '*');
  });
}

function parseCalendarPatientText(text) {
  const patientLine =
    text
      ?.split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line) || '';
  const dobMatch = getDateMatch(patientLine);
  const nameText = dobMatch
    ? patientLine
        .slice(0, dobMatch.index)
        .replace(/^\d{1,2}:\d{2}\s*(am|pm)\s+/i, '')
        .trim()
    : patientLine;
  const cleanName = nameText
    .split(/\s+-\s+/)[0]
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z,\s'-]/g, '')
    .replace(/[-\s]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  let firstName;
  let lastName;
  if (cleanName.includes(',')) {
    const [last, first] = cleanName.split(',', 2);
    firstName = first?.trim() || undefined;
    lastName = last?.trim() || undefined;
  } else {
    const parts = cleanName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      firstName = parts[0];
      lastName = parts.slice(1).join(' ');
    }
  }

  return {
    firstName,
    lastName,
    dob: normalizeCalendarDate(dobMatch?.[1])
  };
}

function getDateMatch(text) {
  return text?.match(
    /\b(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\b/
  );
}

function normalizeCalendarDate(value) {
  if (!value) return undefined;
  const [datePart] = String(value).trim().split('T', 1);

  let year;
  let month;
  let day;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(datePart)) {
    [year, month, day] = datePart.split('-').map(Number);
  } else {
    [month, day, year] = datePart.split(/[/-]/).map(Number);
    if (year < 100) year += year >= 30 ? 1900 : 2000;
  }

  if (!year || !month || !day) return undefined;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getNextScheduleDate(date) {
  const [year, month, day] = date.split('-').map(Number);
  const nextDate = new Date(year, month - 1, day);
  nextDate.setDate(nextDate.getDate() + 1);
  return `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
}

function getScheduleEventStart(value) {
  const match = String(value || '').match(
    /^(\d{4})-(\d{2})-(\d{2})[\sT](\d{1,2}):(\d{2})/
  );
  if (!match) return undefined;

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5])
  );
}

function getScheduleEventDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return undefined;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getScheduleEventTimestamp(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, -1);
}

function getCurrentScheduleDate() {
  const today = new Date();
  const timeZone = getWebPTTimeZone();

  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(today);
      const year = parts.find((part) => part.type === 'year')?.value;
      const month = parts.find((part) => part.type === 'month')?.value;
      const day = parts.find((part) => part.type === 'day')?.value;
      if (year && month && day) return `${year}-${month}-${day}`;
    } catch {
      // Fall back to the browser's local date below.
    }
  }

  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

function getWebPTTimeZone() {
  const accessToken = getCookieValue('prod_access_token');
  const encodedPayload = accessToken?.split('.')[1];
  if (!encodedPayload) return undefined;

  try {
    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(paddedBase64))?.timeZone;
  } catch {
    return undefined;
  }
}

function getCookieValue(name) {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function getDOB() {
  // Find all rows in the table body
  let table = document.querySelector('.patient-text');
  let rows = table?.querySelectorAll('tr') || [];

  // Initialize a variable to store the DOB
  let dob = '05/29/2023';

  // Iterate over each row to find the one that contains the text "DOB:"
  for (let row of rows) {
    if (row.cells[0].textContent.trim() === 'DOB:') {
      // When found, get the text content of the next cell
      dob = row.cells[1].textContent.trim();
      break;
    }
  }

  // Output the DOB
  return dob;
}

function getQuestion(element) {
  var localText = element.parentElement.textContent.trim();
  var parent = element.parentElement;
  var depth = 0;
  while (
    depth < 10 &&
    (parent.textContent.trim() === localText ||
      parent.firstChild.textContent.trim() === '')
  ) {
    parent = parent.parentElement;
    depth++;
  }
  return parent.firstChild.textContent.trim();
}

function getWebPTQuestions(
  forceSection = null,
  isWebPTUser = false,
  noteType = null
) {
  console.log('Getting WebPT Questions');

  let activeTab = document.querySelector('.zpTabsActive')?.textContent?.trim() ?? '';
  if (!activeTab) return {};
  let tabQuestions = document.querySelector(`.zpTab#${activeTab}`);
  if (forceSection) {
    activeTab = forceSection;
  }
  var questionsToKeep = []; // In case we want to filter, assign this list to something useful
  if (activeTab === 'Objective' && (isWebPTUser || noteType === 'Daily Note')) {
    // Find the class = form-text
    let form = document.querySelector('.form-text');
    // loop over each rowdiv in the form
    // Build qText based on each row.textcontent
    // If the rowdiv isa form-sectionHeader start a new question
    // If the rowdiv includes the text AdditionalComments, select Yes, the second choice
    var formQuestion = {};
    var questionKey = '';
    let questionText = '';
    for (let row of form.children) {
      try {
        if (!row.checkVisibility()) {
          continue;
        }
        console.log(row);
        if (row.classList.contains('form-sectionHeader')) {
          // Start a new question
          questionKey = sanitizeQuestionKey(row.textContent);
          questionText = questionKey;
        } else if (row.textContent.includes('Additional Comments')) {
          // Select Yes so that the text area in the next row is showing.
          // Capture leaves the radio alone — it would fight the user's own
          // choice on every poll — and skips the still-hidden textarea below.
          if (!captureScrapeActive) {
            row.querySelectorAll('input')[1].click();
          }
        } else if (
          row.querySelector('textarea') &&
          row.querySelector('textarea').checkVisibility()
        ) {
          // If it is, build the question
          // if the questionKey is empty, skip this row
          if (questionKey === '') {
            continue;
          }

          if (
            questionKey === 'Precautions' ||
            questionKey === 'Objective Findings' ||
            questionKey === 'Pre-Treatment' ||
            questionKey === 'Post-Treatment'
          ) {
            continue;
          }

          // Check if there is any question text by this name already in the formQuestion and if so add a number to it to make it unique
          if (questionText in formQuestion) {
            questionText =
              questionText + ' ' + (Object.keys(formQuestion).length + 1);
          }
          let count = 1;
          let localQuestionKey = questionKey;
          for (const key in formQuestion) {
            if (formQuestion[key].Key === questionKey) {
              count++;
              questionKey = localQuestionKey + ' ' + count;
            }
          }

          console.log('Question: ' + questionKey);
          console.log(row.querySelector('textarea'));
          formQuestion[questionText] = {
            Key: questionKey,
            Type: 'Long Answer',
            ExistingNote: getExistingNote(row.querySelector('textarea').value),
            Input: (val, replyType) => {
              if (replyType === 'append') {
                row.querySelector('textarea').value =
                  row.querySelector('textarea').value + '\n' + val;
              } else if (replyType === 'overwrite') {
                row.querySelector('textarea').value = val;
              }
            }
          };
        } else {
          questionText += '\n' + row.textContent.trim();
        }
      } catch (err) {
        console.error('Error: ' + err);
        getLogFromInjector();
      }
    }

    // Exclude Specific Questions with keys - Neuro-Vascular, Special Tests, Palpation
    // Crystal specific asked for Neuro-Vascular so I assume these boxes are important to some people. With the addition of do-not-reply I feel like we should just include everything
    // for (const [key, value] of Object.entries(formQuestion)) {
    //     if (["Neuro-Vascular", "Special Tests", "Palpation"].includes(value["Key"])) {
    //         delete formQuestion[key]
    //     }
    // }
    // if the length is 0, return activeTab = "Billing"
    if (Object.keys(formQuestion).length === 0) {
      console.log('Billing Section...');
      return getWebPTQuestions('Billing');
    }

    return formQuestion;
  } else if (activeTab === 'Assessment') {
    var formQuestion = {};
    // look for class=tablediv
    let table = tabQuestions.querySelector('.tablediv');
    for (let row of table.children) {
      // console.log(row)
      if (!row.checkVisibility()) {
        continue;
      }
      var existingNote = null;
      var tempOtherLabel = null;
      const radiosInRow = row.querySelectorAll('input[type="radio"]');
      if (radiosInRow && radiosInRow.length) {
        var questionText = row
          .querySelector('.col1div')
          .textContent.replace(':', '')
          .trim();
        var questionType = 'Multiple Choice';
        var options = [];

        const radioResult = processRadioGroup(radiosInRow);
        options = radioResult.choices;
        var questionInput = radioResult.Input;
      } else if (
        row.querySelector('textarea') &&
        row.querySelector('textarea').checkVisibility()
      ) {
        // If a visible textarea exists, treat as Long Answer
        var questionText = row
          .querySelector('.col1div')
          .textContent.replace(':', '')
          .trim();
        // if (questionText === "") { continue }

        var questionType = 'Long Answer';
        existingNote = getExistingNote(row.querySelector('textarea').value);
        var questionInput = (val) => {
          const textarea = row.querySelector('textarea');
          if (tempOtherLabel && textarea && !textarea.checkVisibility()) {
            tempOtherLabel.click();
          }
          if (textarea) textarea.value = val;
        };
      } else if (row.querySelector('select')) {
        // If it is, build the question
        var questionText = row
          .querySelector('.col1div')
          .textContent.replace(':', '')
          .trim();
        var questionType = 'Multiple Choice';
        // choices...
        // This click probably won't work...
        var questionInput = (val) => {
          row.querySelector('select').value = val;
        };
      } else if (row.querySelector('input')) {
        // If it is, build the question
        var questionText = row
          .querySelector('.col1div')
          .textContent.replace(':', '')
          .trim();
        var questionType = 'Multiple Choice';
        // choices...Look for labels not input...
        // This click probably won't work...
        var questionInput = (val) => {
          row.querySelector('input').value = val;
        };
      } else {
        continue;
      }
      if (row.id == 'divProblems') {
        questionKey = 'Problems';
        questionText = 'Problem List';
        questionType = 'Long Answer';
        questionInput = (val) => {
          row.querySelector('textarea').value = val;
        };
      }
      if (questionText === '') {
        continue;
      }
      // Sanitize for required-field text and newlines
      questionText = sanitizeQuestionKey(questionText);

      formQuestion[questionText] = {
        Key: questionText,
        Type: questionType,
        ExistingNote: existingNote,
        Options: options,
        Input: questionInput
      };
      // If we temporarily selected Other to reveal, unselect now after pushing
      if (tempOtherLabel) {
        tempOtherLabel.click();
      }
      console.log(`Question: ${questionText} Type: ${questionType}`);
    }
    return formQuestion;
  } else if (activeTab === 'Plan' && noteType !== 'Daily Note') {
    // Skip Daily Note as flex is handling plan questions
    // var tabQs = tabQuestions.children[1].children

    // Loop over these tabQs, each has 1 question in it, or a column
    // the columns will break into their own
    // Once we get down to rowdiv nopadding we have hit a choice and need to look up a level to get the question

    var questions = {};

    const allInputsAndSelects = tabQuestions.querySelectorAll(
      'input, select, textarea'
    );

    allInputsAndSelects.forEach((inputOrSelect) => {
      // Check if it's visible
      if (inputOrSelect.checkVisibility()) {
        // Look up until we find additional text and grab the first child
        var questionText = getQuestion(inputOrSelect);
        var localText = inputOrSelect.parentElement.textContent.trim();

        console.log('Question: ' + questionText);

        // Way out for this...
        if (
          questionText ===
          "You've entered an incomplete ICD-10 code for this patient."
        ) {
          if (inputOrSelect.tagName === 'TEXTAREA') {
            questionText = 'Plan';
            if (questionText in questions) {
              let count = 1;
              while (questionText + count in questions) {
                count++;
              }
              questionText += count;
            }
          } else {
            return;
          }
        }

        // Check the type
        if (inputOrSelect.tagName === 'SELECT') {
          // If it's a select, get the options and build the question
          var options = inputOrSelect?.querySelectorAll('option') || [];

          var optionsLookup = {};
          options.forEach((option) => {
            optionsLookup[option.textContent] = option.value;
          });

          // Condition the options to list of strings
          options = Array.from(options).map((option) => {
            return option.textContent;
          });
          // remove blank option and "custom"
          options = options.filter((option) => {
            return option !== '' && option !== 'Custom';
          });

          questions[questionText] = {
            Key: questionText,
            Type: 'Multiple Choice',
            Options: options,
            Input: (val) => {
              if (val in optionsLookup) {
                inputOrSelect.value = optionsLookup[val];
              }
            }
          };
        }

        // If it's a select, get the options and build the question
        else if (inputOrSelect.tagName === 'INPUT') {
          // If it's an input check the type for checkbox or radio - that's a choose all
          if (questionText in questions) {
            questions[questionText].Options =
              questions[questionText].Options || [];
            questions[questionText].Choices =
              questions[questionText].Choices || {};
            questions[questionText]['Options'].push(localText);
            questions[questionText]['Choices'][localText] = () => {
              inputOrSelect.click();
            };
          } else {
            questions[questionText] = {
              Key: questionText,
              Type: 'Choose All',
              Options: [localText],
              Choices: {
                [localText]: () => {
                  inputOrSelect.click();
                }
              }
            };
          }
        } else if (inputOrSelect.tagName === 'TEXTAREA') {
          // If it's a textarea, build the question
          questions[questionText] = {
            Key: questionText,
            Type: 'Long Answer',
            Options: [],
            ExistingNote: getExistingNote(inputOrSelect.value),
            Input: (val, replyType) => {
              if (replyType === 'append') {
                inputOrSelect.value = inputOrSelect.value + '\n' + val;
              } else {
                inputOrSelect.value = val;
              }
            }
          };
        }
      }
    });

    // Any questions with only one option are true/false and should be changed...
    Object.keys(questions).forEach((question) => {
      if (questions[question]['Options'].length === 1) {
        questions[question]['Type'] = 'Multiple Choice';
        questions[question]['Options'] = ['True', 'False'];
        questions[question]['Input'] = (val) => {
          const inputFunc = questions[question]['Choices'][''];
          if (val === 'True') {
            inputFunc();
          }
        };
      }
    });

    // Define the list of questions you want to keep
    questionsToKeep = ['Procedures', 'Modalities', 'Specialties'];

    // Assuming 'questions' is an object where keys are question names
    // Filter and reduce the questions object
    var filteredQuestions = Object.keys(questions)
      .filter((key) => questionsToKeep.includes(key))
      .reduce((obj, key) => {
        obj[key] = questions[key];
        return obj;
      }, {});

    // Daily Note...
    if (
      Object.keys(filteredQuestions).length === 0 ||
      Object.keys(filteredQuestions).length === 1
    ) {
      filteredQuestions = {};
      filteredQuestions['Plan'] = {
        Key: 'Plan',
        Type: 'Long Answer',
        ExistingNote: getExistingNote(
          tabQuestions.querySelector('textarea').value
        ),
        Input: (val, replyType) => {
          if (replyType === 'append') {
            tabQuestions.querySelector('textarea').value =
              tabQuestions.querySelector('textarea').value + '\n' + val;
          } else if (replyType === 'overwrite') {
            tabQuestions.querySelector('textarea').value = val;
          }
        }
      };
      return questions;
    }

    return filteredQuestions;
  } else if (activeTab === 'Subjective') {
    var formQuestion = {};
    let sectionHeader = '';
    let table = tabQuestions.querySelector(SELECTORS.tableDiv);

    for (let row of table.children) {
      const result = processRow(row, sectionHeader);
      if (!result) continue;

      if (result.sectionHeader) {
        sectionHeader = result.sectionHeader;
      } else if (result.question) {
        formQuestion[result.questionText] = result.question;
      }
    }
    console.log(formQuestion);
    // Filter formQuestion to only include the questions we want
    questionsToKeep = [
      // Eval Template
      'History of Present Condition/Mechanism of Injury',
      'Primary Concern/Chief Complaint',
      'Prior Level of Function',
      'Current Functional Limitations',
      'Functional Deficits / Gains',
      'Diagnostic Testing/Imaging',
      'Current Complaints / Gains'
    ];
    console.log(formQuestion);
  } else if (activeTab === 'Billing') {
    var formQuestion = {};

    const allInputsAndSelects = tabQuestions.querySelectorAll('textarea');

    allInputsAndSelects.forEach((te) => {
      // Check if it's visible
      try {
        if (te.checkVisibility()) {
          // console.log(te);
          let questionText = null;
          try {
            questionText =
              te.previousElementSibling.previousElementSibling.textContent;
            if (questionText == '') {
              throw new Error('No Question Found');
            }
          } catch (err) {
            // console.log("Error: " + err)
            try {
              questionText = te.previousSibling.previousSibling.textContent;
            } catch (err) {
              try {
                questionText =
                  te.parentElement.parentElement.parentElement.querySelector(
                    'div[class*="form-sectionHeader"]'
                  ).textContent;
              } catch (err) {
                // This one is a billing code, look around for the code
                questionText =
                  te.parentElement.parentElement.parentElement.firstChild.childNodes[1].textContent.trim();
              }
            }
            // console.log("Question: " + questionText)
          }
          if (
            questionText === '' ||
            (questionText !== 'Precautions' &&
              questionText !== 'Objective Findings')
          ) {
            return;
          }
          formQuestion[questionText] = {
            Key: questionText,
            Type: 'Long Answer',
            ExistingNote: getExistingNote(te.value),
            Input: (val, replyType) => {
              if (replyType === 'append') {
                te.value = te.value + '\n' + val;
              } else if (replyType === 'overwrite') {
                te.value = val;
              }
            }
          };
        }
      } catch (err) {
        console.error('Error: ' + err);
        getLogFromInjector();
      }
    });
  }

  let newQuestions = {};
  if (questionsToKeep.length === 0) {
    return formQuestion;
  }

  for (const [key, value] of Object.entries(formQuestion)) {
    if (questionsToKeep.includes(formQuestion[key].Key)) {
      newQuestions[key] = value;
    }
  }
  return newQuestions;
}

function processRadioGroup(radios) {
  const labelOf = (r) =>
    (r.parentElement?.innerText?.trim() || r.value || '').trim();

  const visibleRadios = [...radios].filter(
    (r) => r.checkVisibility?.() ?? true
  );

  const choices = visibleRadios.map(labelOf).filter(Boolean);

  const Input = (val) => {
    for (const r of visibleRadios) {
      const radioLabel = labelOf(r);
      if (radioLabel === val) {
        if (!r.checked) r.click();
      }
    }
  };

  return { choices, Input, visibleRadios };
}

const SELECTORS = {
  tableDiv: '.tablediv',
  col1Div: '.col1div',
  fullWidth: '.full-width',
  dateMonth: 'select[name$="DateMonth"]',
  dateDay: 'select[name$="DateDay"]',
  dateYear: 'select[name$="DateYear"]',
  textarea: 'textarea',
  select: 'select',
  input: 'input',
  label: 'label'
};

function getQuestionTypeAndInput(row) {
  if (row.querySelector(SELECTORS.dateMonth)) {
    return {
      type: 'Date',
      input: (isoTime) => {
        const date = new Date(isoTime);
        // Need month as a number with a leading 0, day as a string, and year as a string
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear().toString();
        row.querySelector(SELECTORS.dateMonth).value = month;
        row.querySelector(SELECTORS.dateDay).value = day;
        row.querySelector(SELECTORS.dateYear).value = year;
      }
    };
  } else if (row.querySelector(SELECTORS.textarea)) {
    // Temporarily click "Other" to reveal textarea if hidden, then unclick before returning
    let textareaList = row.querySelectorAll(SELECTORS.textarea);
    let textarea = null;
    let tempOtherLabel = null;
    if (textareaList.length === 1) {
      textarea = textareaList[0];
    } else if (row.querySelector(SELECTORS.label)) {
      for (let input of row.querySelectorAll(SELECTORS.label)) {
        if (!input.checkVisibility()) {
          continue;
        }
        if (input.textContent.includes('Other')) {
          textarea = input.parentElement.nextElementSibling.querySelector(
            SELECTORS.textarea
          );
          // Capture skips the temporary reveal click; textarea.value still
          // reads fine while hidden, so the value is captured either way.
          if (
            textarea &&
            !textarea.checkVisibility() &&
            !captureScrapeActive
          ) {
            input.click();
            tempOtherLabel = input;
          }
          break;
        }
      }
    }
    if (!textarea) {
      textarea = textareaList[0];
    }

    const result = {
      type: 'Long Answer',
      ExistingNote: getExistingNote(textarea.value),
      input: (val, replyType) => {
        // Ensure visible when applying
        if (tempOtherLabel && !textarea.checkVisibility()) {
          tempOtherLabel.click();
        }
        if (replyType === 'append') {
          textarea.value = textarea.value + '\n' + val;
        } else if (replyType === 'overwrite') {
          textarea.value = val;
        }
      }
    };

    // unclick temporary Other before pushing to questions structure
    if (tempOtherLabel) {
      tempOtherLabel.click();
    }

    return result;
  } else if (row.querySelector(SELECTORS.select)) {
    return {
      type: 'Multiple Choice',
      input: (val) => (row.querySelector(SELECTORS.select).value = val)
    };
  } else if (row.querySelector(SELECTORS.input)) {
    return {
      type: 'Multiple Choice',
      input: (val) => (row.querySelector(SELECTORS.input).value = val)
    };
  } else {
    return {
      type: 'Unknown',
      input: () => {}
    };
  }
}

function processRow(row, sectionHeader) {
  if (!row.checkVisibility()) return null;

  if (row.tagName === 'H3') {
    return { sectionHeader: row.textContent.trim() };
  }

  let questionText = row
    .querySelector(SELECTORS.col1Div)
    ?.textContent.replace(':', '')
    .trim();
  let questionKey = sanitizeQuestionKey(
    questionText ? questionText : sectionHeader
  );

  if (!questionText) questionText = row.innerText.trim();
  // Normalize question text for JSON keys
  questionText = sanitizeQuestionKey(questionText || sectionHeader);

  const ret = getQuestionTypeAndInput(row);
  const type = ret.type;
  if (type === 'Unknown') {
    return null;
  }
  const input = ret.input;

  return {
    question: {
      Key: questionKey,
      Type: type,
      Input: input,
      ExistingNote: ret.ExistingNote
    },
    questionText
  };
}

function populateGoals(goals) {
  // Get the goals div
  var goalsDiv = document.querySelector('#divGoals');
  if (!goalsDiv) {
    return false;
  } // Goals not shown

  // There are 3 fields we want to try to fill in. The Goal Text, The Term, and Timeline.
  // Get the button to add goals
  let addGoal =
    goalsDiv.querySelectorAll('a.link')[
      goalsDiv.querySelectorAll('a.link').length - 1
    ].click;

  // for each of the goals, fill out the fields
  for (var i = 0; i < goals.length; i++) {
    addGoal(); // There is already protection against adding a new goal when the prior goal is empty
    // ... TODO
  }

  // First check if the goals are already populated
  var goalText =
    goalsDiv.querySelectorAll('textarea')[
      goalsDiv.querySelectorAll('textarea').length - 1
    ].value;
  if (goalText !== '') {
  } // Goals already populated, create a new one
  // Get the input
  var goalsInput = goalsDiv.querySelector('input');
  // Set the value
  goalsInput.value = goals;
}

function handleTextInput(el, val, replyType = 'overwrite') {
  if (isEmptyOrNullorSameAsExisting(val, getExistingNote(el.value))) return;
  const sep = replyType === 'append' ? '\n' : '';
  el.value = replyType === 'overwrite' ? val : el.value + sep + val;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

// Parse numeric degrees from API response: "138 degrees", "138 °", "151°", "138"
function parseDegreeValue(val) {
  if (val == null || val === '') return null;
  const valStr = String(val).trim();
  const m =
    valStr.match(/^(\d+)\s*(?:°|degrees?)?\s*$/i) || valStr.match(/^(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

function extractMmtGrade(val) {
  if (val == null) return null;
  // MMT answers can arrive as combined text in either order, e.g.
  // "4-/5; Dynamometry: 10 lb" or "Dynamometry: 10 lb; 4-/5".
  // Pull out only the MMT grade so ROM handlers do not treat it as degrees.
  const m = String(val)
    .trim()
    .match(/(?:^|[\s(;,:])([0-5])\s*([-+]?)\s*\/\s*5(?=$|[\s;,.):])/i);
  return m ? `${m[1]}${m[2]}/5` : null;
}

function isMmtFieldContext(...parts) {
  const context = parts.filter(Boolean).join(' ');
  // Keep MMT normalization scoped to strength-style sections. ROM fields can also mention
  // MMT in comments, but those fields should still prefer the degree value.
  return /(^|[^a-z])(Strength|MMT|Manual Muscle Tests?|Gross Muscle Tests?)([^a-z]|$)/i.test(
    context
  );
}

// Split "151 Degrees, Pain At Endrange" or "143° Pain At End Range" into number-only value + comment for Additional Comments.
function parseNumericAndComment(val) {
  if (val == null || typeof val !== 'string')
    return { numericPart: null, commentPart: null };
  const s = val.trim();
  const m = s.match(/^(\d+)\s*(°|degrees?|%)?\s*[\s,]*(.*)$/i);
  if (!m) return { numericPart: null, commentPart: null };
  const num = m[1];
  const rest = (m[3] || '').trim();
  return { numericPart: num, commentPart: rest || null };
}

function buildROMComment(fieldKey, subFieldKey, numericPart, commentPart) {
  if (!commentPart) return null;

  const clean = (text) =>
    String(text || '')
      .replace(/\s*-\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const cleanComment = String(commentPart)
    .replace(/^[,;:\s-]+/, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleanComment) return null;

  const mainLabel = clean(fieldKey);
  const subLabel = clean(subFieldKey);
  let reference = subLabel || mainLabel;

  if (
    mainLabel &&
    subLabel &&
    !subLabel.toLowerCase().startsWith(mainLabel.toLowerCase())
  ) {
    reference = `${mainLabel} ${subLabel}`;
  }

  const degreeText = numericPart ? `${numericPart} degrees` : '';

  return [reference, degreeText, cleanComment].filter(Boolean).join(' ');
}

// Return true if option text is a range like "121-150" and num is inside it
function optionContainsNumber(optionText, num) {
  const range = (optionText || '').trim().match(/^(\d+)\s*-\s*(\d+)$/);
  if (!range) return false;
  const low = parseInt(range[1], 10);
  const high = parseInt(range[2], 10);
  return num >= low && num <= high;
}

// Parse degree from option label: "(15) °", "15 ° Hyperextension", "0 °", "90" → number or null
function optionTextToDegree(optionText) {
  const t = (optionText || '').trim();
  const m =
    t.match(/^\((\d+)\)\s*°?\s*$/) ||
    t.match(/(\d+)\s*°?\s*Hyperextension$/i) ||
    t.match(/^(\d+)\s*°?\s*$/) ||
    t.match(/^(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

function createSelectInput(select, isObjective = false, isMmtField = false) {
  // Retrieve all <option> elements from the select input as an array
  const options = [...(select?.options || [])];
  // Map each option to its trimmed text content, filtering out empty strings
  let choices = options
    .map((o) => (o.textContent || '').trim())
    .filter(Boolean);
  // If no choices are found, default to a Yes/No choice set
  if (choices.length === 0) {
    choices = ['Yes', 'No'];
  }
  // Find the option whose text content is exactly "Custom", if it exists
  const customOpt = options.find(
    (o) => (o.textContent || '').trim() === 'Custom'
  );
  const selectedText = getExistingNote(
    (select.options[select.selectedIndex]?.textContent || '').trim()
  );
  return {
    'Existing Value': selectedText,
    Choices: customOpt && isObjective ? [] : choices, // If the select has a Custom option, don't show the choices list — AI sends exact value; Custom is the fallback
    Input: (val) => {
      if (val == null || (typeof val === 'string' && val.trim() === '')) return;
      const valStr = String(val).trim();
      const degreeNum = isMmtField ? null : parseDegreeValue(val);
      const mmtGrade = isMmtField ? extractMmtGrade(valStr) : null;

      if (degreeNum != null) {
        // 1) Try exact option match (e.g. "138" or "138 °")
        let opt = options.find(
          (o) => parseDegreeValue((o.textContent || '').trim()) === degreeNum
        );
        // 2) Try option label that parses to same degree (e.g. "(15) °", "15 ° Hyperextension")
        if (!opt) {
          opt = options.find(
            (o) =>
              optionTextToDegree((o.textContent || '').trim()) === degreeNum
          );
        }
        // 3) Try range option that contains this number (e.g. "121-150" for 138)
        if (!opt) {
          opt = options.find((o) =>
            optionContainsNumber((o.textContent || '').trim(), degreeNum)
          );
        }
        // 4) No matching option: use Custom + degree text input; if unit dropdown (°/%) exists, set number only + select °
        if (!opt && customOpt && isObjective) {
          select.value = customOpt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          const container =
            select.closest('.col2div') ||
            select.closest('.colLeftdiv') ||
            select.closest('.colRightdiv') ||
            select.parentElement;
          // Try to find a secondary "unit" dropdown (e.g., for °/% selection) in the same container as the select
          const unitSelect = container?.querySelectorAll?.('select')
            ? [...(container.querySelectorAll('select') || [])].find((s) => {
                // Get option labels for this secondary select
                const opts = [...(s.options || [])].map((o) =>
                  (o.textContent || '').trim()
                );
                // We're looking for a select with both '°' and '%' as options
                return opts.includes('°') && opts.includes('%');
              })
            : null;

          // If such a unit select exists, we will treat only the numeric value as the degreeText; otherwise, include the '°' symbol.
          const numberOnly = !!unitSelect; // If unitSelect is present, only use the number
          const degreeText = numberOnly ? String(degreeNum) : degreeNum + ' °';

          // Fills the associated custom input(s) for the value, possibly multiple times to overcome race conditions/UI rendering delays
          const fill = () => {
            // Priority:
            // 1. Explicit custom textbox by id (id='Txt' + select.id) if available
            // 2. Any text box within the same column div (col2div, colRightdiv, or colLeftdiv)
            // 3. As a fallback, a text box in the next sibling element (for uncommon layouts)
            const colContainer =
              select.closest('.col2div') ||
              select.closest('.colRightdiv') ||
              select.closest('.colLeftdiv');
            const el =
              document.getElementById('Txt' + select.id) ||
              colContainer?.querySelector('input[type="text"]') ||
              (select.parentElement?.nextElementSibling?.querySelector?.(
                'input'
              ) ??
                null);
            // If we found a corresponding text field, set its value and dispatch an input event to ensure UI updates
            if (el) {
              el.value = degreeText;
              el.dispatchEvent(new Event('input', { bubbles: true }));
            }
            // If there is a unit select, also set it to '°' (degree symbol)
            if (unitSelect) {
              const degOpt = [...(unitSelect.options || [])].find(
                (o) => (o.textContent || '').trim() === '°'
              );
              if (degOpt) {
                unitSelect.value = degOpt.value;
                unitSelect.dispatchEvent(
                  new Event('change', { bubbles: true })
                );
              }
            }
          };

          // Call fill immediately and again after short delay to overcome race conditions with UI updates
          fill();
          setTimeout(fill, 80); // Re-attempt shortly after (for React rendering)
          return;
        }
        // If an option did match, select it and fire a change event
        if (opt) {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      // Non-numeric: match option (case-insensitive so API "normal muscular" matches "Normal Muscular")
      const exactOpt = options.find(
        (o) =>
          (o.textContent || '').trim().toLowerCase() === valStr.toLowerCase()
      );
      if (exactOpt) {
        select.value = exactOpt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // No match but select has Custom (e.g. Endfeel): select Custom and fill the custom text input — only in Objective
      if (valStr && customOpt && isObjective) {
        select.value = customOpt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        // Find the most appropriate container for the custom text input:
        // Priority:
        //   1. Check for an element with id 'Txt' + select.id (the traditional custom input)
        //   2. Search for a text input within the closest column div (.col2div, .colLeftdiv, .colRightdiv)
        //   3. As a fallback, look for a text input in the next sibling of select's parent (for edge cases)
        const container =
          select.closest('.col2div') ||
          select.closest('.colLeftdiv') ||
          select.closest('.colRightdiv') ||
          select.parentElement;
        const el =
          document.getElementById('Txt' + select.id) ||
          container?.querySelector('input[type="text"]') ||
          (select.parentElement?.nextElementSibling?.querySelector?.('input') ??
            null);

        // In Objective: strip trailing commentary for MMT grades or numeric degree values.
        // Only apply MMT extraction for known strength/MMT fields so ROM fields still prefer degrees.
        // Fall back to capitalized full string if no leading number is found.
        if (el) {
          const numMatch = valStr.match(/^(\d+)\s*(?:°|degrees?)?\s*/i);
          let fillValue = valStr.replace(/\b\w/g, (c) => c.toUpperCase());
          if (mmtGrade) {
            fillValue = mmtGrade;
          } else if (isMmtField) {
            fillValue = valStr;
          } else if (numMatch) {
            fillValue = numMatch[1] + '°';
          }
          el.value = fillValue;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }
  };
}

// COM-558: the comment box under each billed CPT code (shown once units are
// entered). WebPT pre-fills it with the previous visit's comment.
function isCptCommentBox(te) {
  return /^description[0-9A-Z]+_\d+$/.test(te?.id || '');
}

// The prior comment describes the previous visit, so it is not sent as an
// existing value (the model would answer "None" and keep it). A None/null
// answer leaves the box untouched in either reply mode.
function cptCommentInteraction(te) {
  const Input = (val, replyType = 'overwrite') => {
    if (val == null || val === '' || val === 'None') return;
    if (replyType === 'append') {
      te.value = te.value ? te.value + '\n' + val : val;
    } else {
      te.value = val;
    }
  };
  // Capture records what the note holds, so it still reads the comment.
  return {
    'Existing Value': captureScrapeActive ? getExistingNote(te.value) : null,
    Input
  };
}

function extractBillingAndObjectiveQuestions(tabContent, isHidden) {
  const allTextareas = tabContent.querySelectorAll('textarea');
  // Track section headers as we process
  const sectionHeaders = Array.from(
    tabContent.querySelectorAll('.form-sectionHeader')
  ).map((h) => ({
    element: h,
    text: h.textContent?.trim() || '',
    nextSibling: h.nextElementSibling
  }));
  // Group textareas by section
  const sectionFields: Record<
    string,
    Array<{ textarea: HTMLTextAreaElement; questionText: string }>
  > = {};
  allTextareas.forEach((te) => {
    // Check if it's visible
    try {
      if (te.checkVisibility && !te.checkVisibility()) return;
      if (isHidden(te)) return;
      // Determine current section by finding the nearest section header above this textarea
      let currentSection = 'Billing Sheet';
      for (const section of sectionHeaders) {
        if (
          section.element.compareDocumentPosition(te) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          currentSection = section.text || 'Billing Sheet';
        }
      }
      let questionText = null;

      // Try multiple strategies to find the question text
      // Strategy 1: Look for text nodes in previous siblings (handles "Pre-Treatment<br><textarea> pattern)
      if (!questionText) {
        let node = te.previousSibling;
        let checkedCount = 0;
        // Check up to 3 previous siblings to find text
        while (node && checkedCount < 3) {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            if (text && text.length > 0 && !text.match(/^[\s\n\r]*$/)) {
              questionText = text;
              break;
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            // If it's a <br>, check the previous sibling for text
            const element = node as Element;
            if (element.tagName === 'BR') {
              const prevTextNode = node.previousSibling;
              if (prevTextNode && prevTextNode.nodeType === Node.TEXT_NODE) {
                const text = prevTextNode.textContent?.trim();
                if (text && text.length > 0 && !text.match(/^[\s\n\r]*$/)) {
                  questionText = text;
                  break;
                }
              }
            } else {
              // For other elements, check their text content
              const text = element.textContent?.trim();
              if (text && text.length > 0 && !text.match(/^[\s\n\r]*$/)) {
                questionText = text;
                break;
              }
            }
          }
          node = node.previousSibling;
          checkedCount++;
        }
      }
      // Strategy 2: Look for label before textarea (common pattern)
      if (!questionText) {
        const label = te.previousElementSibling?.previousElementSibling;
        if (label && label.textContent?.trim()) {
          questionText = label.textContent.trim();
        }
      }
      // Strategy 3: Look for label element before textarea
      if (!questionText) {
        let prev = te.previousElementSibling;
        while (prev) {
          if (prev.tagName === 'LABEL' || prev.tagName === 'B') {
            questionText = prev.textContent?.trim();
            if (questionText) break;
          }
          prev = prev.previousElementSibling;
        }
      }
      // Strategy 4: Look for label in parent row structure
      if (!questionText) {
        const row = te.closest('.rowdiv');
        if (row) {
          const col1 = row.querySelector('.col1div');
          if (col1 && col1.textContent?.trim()) {
            questionText = col1.textContent.trim().replace(':', '').trim();
          }
        }
      }
      // Strategy 5: Look for section header in parent structure
      if (!questionText) {
        const sectionHeader = te
          .closest('.rowdiv')
          ?.previousElementSibling?.querySelector('.form-sectionHeader');
        if (sectionHeader && sectionHeader.textContent?.trim()) {
          questionText = sectionHeader.textContent.trim();
        }
      }
      // Strategy 6: For billing codes, look for the code label
      if (!questionText) {
        const parent = te.parentElement?.parentElement?.parentElement;
        if (parent) {
          const codeLabel =
            parent.firstChild?.childNodes[1]?.textContent?.trim();
          if (codeLabel) {
            questionText = codeLabel;
          }
        }
      }
      // Strategy 7: Extract from textarea id/name by converting camelCase/PascalCase to readable text
      if (!questionText) {
        const id = te.name || te.id || '';
        if (id) {
          // Convert camelCase/PascalCase to space-separated text
          // e.g., "ObjectiveFindingsPreTreatment" -> "Objective Findings Pre Treatment"
          questionText = id
            .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space between consecutive capitals
            .trim();
        }
      }
      // Strategy 8: Use textarea name or id as fallback
      if (!questionText) {
        questionText = te.name || te.id || 'Untitled Field';
      }
      if (!questionText || questionText === '') {
        return;
      }

      // Clean up question text
      questionText = questionText.replace(/^\s*:\s*/, '').trim();
      // Group by section
      if (!sectionFields[currentSection]) {
        sectionFields[currentSection] = [];
      }
      sectionFields[currentSection].push({ textarea: te, questionText });
    } catch (err) {
      console.error('Error processing billing textarea: ' + err);
    }
  });
  // Separate Pre-Treatment and Post-Treatment fields for the "Pre/post Treatment" section
  const prePostTreatmentFields = [];
  const otherFields = {};

  // First pass: separate Pre-Treatment and Post-Treatment from other fields
  Object.entries(sectionFields).forEach(([sectionName, fields]) => {
    if (sectionName === 'Untitled Section' && fields.length === 0) return;

    fields.forEach(({ textarea: te, questionText }) => {
      const normalizedText = questionText.trim();
      // Check if this is Pre-Treatment or Post-Treatment
      if (
        normalizedText === 'Pre-Treatment' ||
        normalizedText === 'Post-Treatment'
      ) {
        prePostTreatmentFields.push({ textarea: te, questionText });
      } else if (
        normalizedText !== 'Precautions' &&
        normalizedText !== 'Objective Findings'
      ) {
        // Exclude Precautions and Objective Findings, but include other fields
        if (!otherFields[sectionName]) {
          otherFields[sectionName] = [];
        }
        otherFields[sectionName].push({ textarea: te, questionText });
      }
      // Precautions and Objective Findings are excluded (not added to any structure)
    });
  });

  // Create "Pre/post Treatment" section if we have Pre-Treatment or Post-Treatment fields
  if (prePostTreatmentFields.length > 0) {
    const prePostSectionKey = 'Pre/post Treatment';
    const prePostLabelTracker: Record<string, number> = {};
    const getPrePostUniqueKey = (baseLabel: string) => {
      if (!prePostLabelTracker[baseLabel]) {
        prePostLabelTracker[baseLabel] = 1;
        return baseLabel;
      } else {
        prePostLabelTracker[baseLabel]++;
        return `${baseLabel} (${prePostLabelTracker[baseLabel]})`;
      }
    };

    questionInteractions[prePostSectionKey] = {
      Key: prePostSectionKey,
      Question: prePostSectionKey,
      Type: 'Flex',
      Structure: { [prePostSectionKey]: {} },
      Elements: []
    };
    const prePostStructure =
      questionInteractions[prePostSectionKey].Structure[prePostSectionKey];

    prePostTreatmentFields.forEach(({ textarea: te, questionText }) => {
      const uniqueKey = getPrePostUniqueKey(questionText);
      prePostStructure[uniqueKey] = {
        'Existing Value': getExistingNote(te.value),
        Input: (val, replyType = 'overwrite') => {
          if (replyType === 'append') {
            te.value = te.value + '\n' + val;
          } else if (replyType === 'overwrite') {
            te.value = val;
          }
        }
      };
    });
  }

  // Create separate question for each remaining section (excluding Precautions, Objective Findings, Pre-Treatment, Post-Treatment)
  Object.entries(otherFields).forEach(([sectionName, fields]) => {
    if (fields.length === 0) return;
    const sectionKey = sectionName || 'Billing';
    const sectionLabelTracker: Record<string, number> = {};
    const getSectionUniqueKey = (baseLabel: string) => {
      if (!sectionLabelTracker[baseLabel]) {
        sectionLabelTracker[baseLabel] = 1;
        return baseLabel;
      } else {
        sectionLabelTracker[baseLabel]++;
        return `${baseLabel} (${sectionLabelTracker[baseLabel]})`;
      }
    };
    // Create question interaction for this section
    questionInteractions[sectionKey] = {
      Key: sectionKey,
      Question: sectionKey,
      Type: 'Flex',
      Structure: { [sectionKey]: {} },
      Elements: []
    };
    const sectionStructure =
      questionInteractions[sectionKey].Structure[sectionKey];
    // Add fields directly to the section structure
    fields.forEach(({ textarea: te, questionText }) => {
      const uniqueKey = getSectionUniqueKey(questionText);
      if (isCptCommentBox(te)) {
        sectionStructure[uniqueKey] = cptCommentInteraction(te);
        return;
      }
      sectionStructure[uniqueKey] = {
        'Existing Value': getExistingNote(te.value),
        Input: (val, replyType = 'overwrite') => {
          if (replyType === 'append') {
            te.value = te.value + '\n' + val;
          } else if (replyType === 'overwrite') {
            te.value = val;
          }
        }
      };
    });
  });
  // Remove the top-level "Billing" question if it exists, since we've created separate questions for each section
  if (questionInteractions['Billing']) {
    delete questionInteractions['Billing'];
  }
  if (questionInteractions['Objective']) {
    delete questionInteractions['Objective'];
  }
}

function extractFlexQuestions(isWebPTUser = false, noteType) {
  const activeTabLabel = document
    .querySelector('.zpTabsActive')
    ?.textContent?.trim();
  if (!activeTabLabel) return;
  if (['Assessment'].includes(activeTabLabel)) return;

  const tabContent = document.querySelector(`.zpTab#${activeTabLabel}`);
  if (!tabContent) return;

  // Helper: treat any element with .hidden (or inside .hidden) as hidden
  const isHidden = (el) =>
    !!el && (el.classList?.contains('hidden') || el.closest('.hidden'));

  const key = activeTabLabel;
  if (!questionInteractions[key]) {
    questionInteractions[key] = {
      Key: key,
      Question: key,
      Type: 'Flex',
      Structure: {},
      Elements: []
    };
  }
  const excludeRows =
    activeTabLabel === 'Subjective' || activeTabLabel === 'Objective';

  // Exclude hidden rows at the source
  const allRows = excludeRows
    ? [
        ...tabContent.querySelectorAll(
          '.rowdiv:not(.hidden), h3.with-border:not(.hidden)'
        )
      ]
    : [...tabContent.querySelectorAll('.rowdiv:not(.hidden)')];

  if (!questionInteractions[key].Structure) {
    questionInteractions[key].Structure = {};
  }
  const structure = questionInteractions[key].Structure;

  let currentSection = 'Untitled Section';
  const labelTracker = {};

  const getUniqueKey = (baseLabel) => {
    if (!labelTracker[baseLabel]) {
      labelTracker[baseLabel] = 1;
      return baseLabel;
    } else {
      labelTracker[baseLabel]++;
      return `${baseLabel} (${labelTracker[baseLabel]})`;
    }
  };

  const getLabelPrefix = (currentRow) => {
    const parent = currentRow.parentElement;
    const outerRow = parent?.closest('.rowdiv');
    const parentRow = outerRow?.previousElementSibling;
    if (!parentRow || isHidden(parentRow)) return null;

    const label = parentRow?.querySelector('.col1div')?.innerText?.trim();

    // Only return meaningful headers (ignore blanks and directional labels)
    if (
      label &&
      !['Left', 'Right', 'Apply to All', ''].includes(label) &&
      !/^\d+$/.test(label)
    ) {
      return label;
    }

    return null;
  };

  const createCheckboxInput = (map) => {
    const choices =
      Object.keys(map).length > 0 ? Object.keys(map) : ['Yes', 'No'];
    const checkedLabels = Object.entries(map)
      .filter(([, cb]) => (cb as HTMLInputElement).checked)
      .map(([lbl]) => lbl);
    return markMultiChoice({
      'Existing Value':
        checkedLabels.length > 0 ? checkedLabels.join('$') : null,
      Description: `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. The available options are: ${choices.join(', ')}`,
      Input: (vals) => {
        if (!vals) return;
        Object.entries(map).forEach(([lbl, cb]) => {
          if (isHidden(cb)) return;
          const shouldBeChecked = vals.includes(lbl);
          if (shouldBeChecked && !cb.checked) {
            cb.click();
          }
        });
      }
    }, Object.keys(map));
  };

  // Helper: detect if a radio group would reveal conditional content
  const hasConditionalChildrenForRadios = (container, radiosNodeList) => {
    const radiosArr = [...radiosNodeList];

    // Attribute-linked containers (e.g., aria-controls, data-target)
    for (const r of radiosArr) {
      const ids = [
        r.getAttribute('aria-controls'),
        r.getAttribute('data-controls'),
        r.getAttribute('data-target'),
        r.getAttribute('data-child')
      ].filter(Boolean);

      for (const id of ids) {
        const el =
          id && container.ownerDocument.querySelector(`#${CSS.escape(id)}`);
        if (el && !isHidden(el)) return true;
        // Even if currently hidden, it implies conditional content exists
        if (el) return true;
      }
    }

    // Structural hints of conditional/nested UI inside the same container
    if (
      container.querySelector(
        '.indented, .nested, .conditional, .child, .expandable, .inset-tests'
      )
    )
      return true;

    // Any extra inputs beyond radios (text/select/textarea/tables) in this container
    const extra = [
      ...container.querySelectorAll(
        'select, textarea, input:not([type="radio"]):not([type="hidden"]), table'
      )
    ].filter((el) => !isHidden(el));
    if (extra.length) return true;

    // Adjacent indented/conditional sibling blocks tied to this row
    const row = container.closest('.rowdiv');
    const next = row?.nextElementSibling;
    if (
      next &&
      (next.classList.contains('indented') ||
        next.matches('.conditional, .nested, .expandable, .inset-tests'))
    ) {
      return true; // even if hidden, it implies conditional content exists
    }
    // or in nextElementSibling
    if (next && next.classList.contains('hidden')) {
      return true;
    }

    return false;
  };

  function normalizeTimeLabel(label) {
    if (label.startsWith('Time In/Time Out') && label.includes('-')) {
      return label; // keep the parent prefix intact
    }
    return label;
  }

  const extractCheckboxMapFromTable = (table) => {
    const map = {};
    if (!table || isHidden(table)) return map;
    table.querySelectorAll('tr').forEach((tr) => {
      if (isHidden(tr)) return;
      const cb = tr.querySelector('input[type="checkbox"]');
      if (isHidden(cb)) return;
      const lbl =
        tr.querySelector('label')?.innerText?.trim() || tr.innerText?.trim();
      if (cb && lbl) map[lbl] = cb;
    });
    return map;
  };

  // Generic processor for dense radio-based scale blocks (e.g., pain scales)
  function processDenseRadioBlocks(tabRoot, section) {
    if (!tabRoot) return;

    // A candidate block is any container that has rows with many radios laid out inline
    const containers = Array.from(
      tabRoot.querySelectorAll('.custom-test-template, .rowdiv')
    ).filter((el) => !isHidden(el));

    let groupCounter = 0;

    const getSuffixFromAnyId = (root) => {
      const any = root.querySelector('[id*="_"]');
      const m = any?.id?.match(/_(\d+)\b/);
      return m ? m[1] : null;
    };

    const radiosToChoices = (nodeList) =>
      Array.from(nodeList)
        .filter((r) => !isHidden(r))
        .map((r) =>
          (r.parentElement?.textContent?.trim() || r.value || '').trim()
        )
        .filter(Boolean);

    containers.forEach((container) => {
      // Heuristic: must contain at least one row with > 6 radios in col2div
      const rows = Array.from(container.querySelectorAll('.rowdiv')).filter(
        (r) => !isHidden(r)
      );
      const radioRows = rows.filter((r) => {
        const c2 = r.querySelector('.col2div');
        if (!c2 || isHidden(c2)) return false;
        const radios = c2.querySelectorAll('input[type="radio"]');
        if (!radios.length) return false;
        // radios sharing the same name form a scale; require >= 6 to avoid yes/no
        const nameCounts = {};
        Array.from(radios).forEach((rd) => {
          nameCounts[rd.name] = (nameCounts[rd.name] || 0) + 1;
        });
        return Object.values(nameCounts).some((cnt) => cnt >= 6);
      });

      if (!radioRows.length) return; // not a dense scale block

      // Create a stable group name using a numeric suffix if present, otherwise a counter
      const suffix = getSuffixFromAnyId(container) || String(++groupCounter);
      const group = `Pain Scale ${suffix}`;

      // Optional free-text location at the top of many scale blocks
      const locationInput = container.querySelector(
        '.rowdiv .col1div + .col2div input[type="text"]'
      );
      const locationLabel =
        container.querySelector('.rowdiv .col1div')?.textContent?.trim() ||
        'Location';
      if (locationInput && !isHidden(locationInput)) {
        section[`${group} - ${locationLabel}`] = {
          'Existing Value': getExistingNote(locationInput.value),
          Input: (val, replyType = 'overwrite') =>
            handleTextInput(locationInput, val, replyType)
        };
      }

      // For each radio row, use its left label (col1div) as the key and build choices from labels/values
      radioRows.forEach((r) => {
        const key =
          (r.querySelector('.col1div')?.textContent || '')
            .replace(/:$/, '')
            .trim() || 'Scale';
        const radios = r.querySelectorAll('input[type="radio"]');
        const choices = radiosToChoices(radios);
        section[`${group} - ${key}`] = {
          Choices: choices,
          Input: (val) => {
            Array.from(radios).forEach((rd) => {
              const labelOrVal = (
                rd.parentElement?.textContent?.trim() ||
                rd.value ||
                ''
              ).trim();
              if (String(val) === labelOrVal && !rd.checked) rd.click();
            });
          }
        };
      });

      // Common pattern: a select (e.g., Pain Description) followed by an optional custom text input
      const selects = Array.from(container.querySelectorAll('select')).filter(
        (s) => !isHidden(s)
      );
      selects.forEach((sel) => {
        const labelEl = sel.closest('.col2div')?.previousElementSibling;
        const key =
          (labelEl?.textContent || '').replace(/:$/, '').trim() || 'Selection';
        const choices = Array.from(sel.options)
          .map((o) => o.textContent?.trim() || '')
          .filter(Boolean);
        section[`${group} - ${key}`] = {
          Choices: choices,
          Input: (val) => {
            const opt = Array.from(sel.options).find(
              (o) => (o.textContent || '').trim() === String(val)
            );
            if (opt) {
              sel.value = opt.value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        };
        // Paired custom input that often follows the select
        const maybeCustom =
          sel.parentElement?.querySelector('input[type="text"]');
        if (maybeCustom && !isHidden(maybeCustom)) {
          section[`${group} - ${key} - Follow-up Plan`] = {
            Input: (val, replyType = 'overwrite') =>
              handleTextInput(maybeCustom, val, replyType)
          };
        }
      });

      // Revealed details: if a Yes/No YN block toggles a div with a multi-select and/or custom text
      const revealedBlocks = Array.from(
        container.querySelectorAll('.rowdiv[id^="div"], .rowdiv.hidden[id]')
      );
      revealedBlocks.forEach((block) => {
        if (!block.id) return;
        // multi-select
        const multi = block.querySelector('select[multiple]');
        if (multi && !isHidden(multi)) {
          const parentLabel =
            block
              .closest('.rowdiv')
              ?.previousElementSibling?.querySelector('.col1div')
              ?.textContent?.trim() || 'Details';
          const opts = Array.from(multi.options)
            .map((o) => o.textContent?.trim() || '')
            .filter(Boolean);
          section[`${group} - ${parentLabel} - Details`] = {
            Choices: opts,
            Input: (vals) => {
              const want = Array.isArray(vals)
                ? vals
                : String(vals)
                    .split('$')
                    .map((v) => v.trim());
              // Additive: answerFlexQuestion calls Input once per '$' item.
              Array.from(multi.options).forEach((o) => {
                if (want.includes((o.textContent || '').trim())) o.selected = true;
              });
              multi.dispatchEvent(new Event('change', { bubbles: true }));
            },
            Clear: () => Array.from(multi.options).forEach((o) => (o.selected = false))
          };
          markMultiChoice(section[`${group} - ${parentLabel} - Details`], opts);
        }
        const custom = block.querySelector('input[type="text"]');
        if (custom && !isHidden(custom)) {
          const parentLabel =
            block
              .closest('.rowdiv')
              ?.previousElementSibling?.querySelector('.col1div')
              ?.textContent?.trim() || 'Details';
          section[`${group} - ${parentLabel} - Details (Custom)`] = {
            Input: (val, replyType = 'overwrite') =>
              handleTextInput(custom, val, replyType)
          };
        }
      });
    });
  }

  if (
    activeTabLabel === 'Billing' ||
    (activeTabLabel == 'Objective' && noteType == 'Daily Note')
  ) {
    extractBillingAndObjectiveQuestions(tabContent, isHidden);
    return; // Exit early for billing since we've processed it
  }

  allRows.forEach((row, rowIdx, rowsArr) => {
    // Skip rows that belong to a processed dense radio block (e.g., .custom-test-template)
    if (row.closest('.custom-test-template')) return;
    if (isHidden(row)) return;

    if (row.classList.contains('form-sectionHeader')) {
      const label = row.querySelector('div')?.innerText?.trim();
      if (label) currentSection = sanitizeQuestionKey(label);
      return;
    }
    if (excludeRows && row.matches('h3.with-border')) {
      const sectionLabel = row.textContent?.trim();
      if (sectionLabel && !sectionLabel.toLowerCase().includes('untitled')) {
        currentSection = sanitizeQuestionKey(sectionLabel);
      }
      return;
    }

    if (!structure[currentSection]) structure[currentSection] = {};
    const section = structure[currentSection];

    const col1 = row.querySelector('.col1div');
    const col2 = row.querySelector('.col2div');
    const colLeft = row.querySelector('.colLeftdiv');
    const colRight = row.querySelector('.colRightdiv');

    const rawLabel = isHidden(col1) ? '' : col1?.innerText?.trim() || '';
    const labelPrefix = getLabelPrefix(row);
    const baseLabel =
      !rawLabel || rawLabel === '\u00A0' ? 'Untitled' : rawLabel;
    let label = labelPrefix ? `${labelPrefix} - ${baseLabel}` : baseLabel;
    label = sanitizeQuestionKey(normalizeTimeLabel(label));
    if (
      isWebPTUser &&
      (label.includes('Comments') ||
        label.includes('Followup') ||
        label.includes('Inspection'))
    ) {
      return;
    }
    if (
      label.includes(
        'This patient is eligible for Outcomes. Please select a questionnaire'
      )
    ) {
      return;
    }
    const questionsToKeep = [
      // Eval Template
      'History of Present Condition/Mechanism of Injury',
      'Primary Concern/Chief Complaint',
      'Prior Level of Function',
      'Current Functional Limitations',
      'Functional Deficits / Gains',
      'Diagnostic Testing/Imaging',

      // Daily Template
      'Current Complaints / Gains'
    ];
    label = label.replace(/:$/, '').trim();
    if (questionsToKeep.includes(label)) return;

    // Exclude Basic Info profile-type selector (two options separated by /) from injector output
    // if (label === 'Pelvic Profile/Speech Profile') return;
    if (label.includes('Pelvic Profile')) return;
    if (label.includes('Speech Profile')) return;

    // ---- FLEX DATE FIELD HANDLING ----
    // Look for three consecutive <select>s inside col2 for date (Month/Day/Year)
    if (col2 && !isHidden(col2)) {
      const selects = Array.from(col2.querySelectorAll('select')).filter(
        (s) => !isHidden(s)
      );
      // Find three consecutive visible selects
      if (selects.length >= 3) {
        for (let i = 0; i <= selects.length - 3; i++) {
          // Heuristically: if the three selects are adjacent siblings in col2
          if (
            selects[i].nextElementSibling === selects[i + 1] &&
            selects[i + 1].nextElementSibling === selects[i + 2]
          ) {
            // Assign Month/Day/Year by position
            const dateParts = ['Month', 'Day', 'Year'];
            for (let j = 0; j < 3; j++) {
              section[getUniqueKey(`${label} - ${dateParts[j]}`)] =
                createSelectInput(
                  selects[i + j],
                  activeTabLabel === 'Objective'
                );
            }
            // Skip the next two <select>s as they're part of this date
            return;
          }
        }
      }
    }

    // ---- FLEX TIME FIELD HANDLING ----
    // Look for an input[type="text"] immediately followed by a select with AM/PM options
    if (col2 && !isHidden(col2)) {
      const textInputs = Array.from(
        col2.querySelectorAll('input[type="text"]')
      ).filter((s) => !isHidden(s));
      const selects = Array.from(col2.querySelectorAll('select')).filter(
        (s) => !isHidden(s)
      );
      // Try to match text input followed by select with AM/PM
      if (textInputs.length && selects.length) {
        for (let i = 0; i < textInputs.length; i++) {
          const tInput = textInputs[i];
          // Next sibling that is a select
          let next = tInput.nextElementSibling;
          while (next && !(next.tagName === 'SELECT'))
            next = next.nextElementSibling;
          if (next && next.tagName === 'SELECT') {
            // Check if select options are AM/PM
            const opts = Array.from(next.options).map((o) =>
              o.textContent?.trim().toUpperCase()
            );
            if (opts.includes('AM') && opts.includes('PM')) {
              // Label as "<Label> - Time" and "<Label> - AM/PM"
              section[getUniqueKey(`${label} - Time`)] = {
                'Existing Value': getExistingNote(
                  (tInput as HTMLInputElement).value
                ),
                Input: (val, replyType = 'overwrite') =>
                  handleTextInput(tInput, val, replyType)
              };
              section[getUniqueKey(`${label} - AM/PM`)] = createSelectInput(
                next,
                activeTabLabel === 'Objective'
              );
              return;
            }
          }
        }
      }
    }

    // ---- FLEX CHECKBOX TABLE HANDLING ----
    if (col2 && !isHidden(col2)) {
      const table = col2.querySelector('table');
      if (table && !isHidden(table)) {
        const checkboxes = table.querySelectorAll('input[type="checkbox"]');
        if (checkboxes.length) {
          const map = {};
          checkboxes.forEach((cb) => {
            if (isHidden(cb)) return;
            let lbl = cb.closest('label')?.innerText?.trim() || null;

            // Otherwise check the same cell or next cell text/label
            if (!lbl) {
              const td = cb.closest('td');
              if (td) {
                const nextCell = td.nextElementSibling;
                if (nextCell) {
                  const lblEl = nextCell.querySelector('label');
                  if (lblEl && lblEl.innerText.trim()) {
                    lbl = lblEl.innerText.trim();
                  } else if (nextCell.innerText.trim()) {
                    lbl = nextCell.innerText.trim();
                  }
                }
              }
            }
            // Fallback: value or empty string
            if (!lbl) {
              lbl = cb.value?.trim() || '';
            }
            if (!lbl) return;
            map[lbl] = cb;

            // look for matching indented <textarea> by id pattern div<ID>
            const id = cb.id;
            if (id) {
              const followBlock = document.querySelector(`#div${id}`);
              const ta = followBlock?.querySelector('textarea');
              if (ta && !isHidden(ta)) {
                // attach a secondary input for this checkbox's notes
                section[getUniqueKey(`${label} - ${lbl} Notes`)] = {
                  'Existing Value': getExistingNote(
                    (ta as HTMLInputElement).value
                  ),
                  Input: (val, replyType = 'overwrite') =>
                    handleTextInput(ta, val, replyType)
                };
              }
            }
          });

          // register the whole group as a checkbox input
          const checkedTableLabels = Object.entries(map)
            .filter(([, cb]) => (cb as HTMLInputElement).checked)
            .map(([lbl]) => lbl);
          section[getUniqueKey(label)] = markMultiChoice({
            'Existing Value':
              checkedTableLabels.length > 0
                ? checkedTableLabels.join('$')
                : null,
            Description: `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. The available options are: ${Object.keys(map).join(', ')}`,
            Input: (vals) => {
              const want = Array.isArray(vals)
                ? vals
                : String(vals)
                    .split('$')
                    .map((v) => v.trim());
              Object.entries(map).forEach(([lbl, cb]) => {
                const shouldCheck = want.includes(lbl);
                if (shouldCheck && !cb.checked) {
                  cb.click();
                }
              });
            }
          }, Object.keys(map));

          return; // stop further processing of this row
        }
      }
      // --- FLEX CHECKBOX-ONLY ROWS WITH INDENTED HIDDEN BLOCKS (e.g., Current Medications) ---
      // Look for .rowdiv.nopadding > label > input[type="checkbox"] pattern inside col2
      const singleCheckboxRows = col2.querySelectorAll(
        '.rowdiv.nopadding > label > input[type="checkbox"]'
      );
      if (singleCheckboxRows.length) {
        const map = {};
        singleCheckboxRows.forEach((cb) => {
          if (isHidden(cb)) return;
          let lbl = cb.closest('label')?.innerText?.trim() || '';
          if (!lbl) return;
          map[lbl] = cb;

          // Look for paired indented block with id pattern "div<ID>"
          if (cb.id) {
            const followBlock = document.querySelector(`#div${cb.id}`);
            const ta = followBlock?.querySelector(
              'input[type="text"], textarea'
            );
            if (ta && !isHidden(ta)) {
              section[getUniqueKey(`${label} - ${lbl} Notes`)] = {
                'Existing Value': getExistingNote(
                  (ta as HTMLInputElement).value
                ),
                Input: (val, replyType = 'overwrite') =>
                  handleTextInput(ta, val, replyType)
              };
            }
          }
        });

        const checkedSingleLabels = Object.entries(map)
          .filter(([, cb]) => (cb as HTMLInputElement).checked)
          .map(([lbl]) => lbl);
        section[getUniqueKey(label)] = markMultiChoice({
          'Existing Value':
            checkedSingleLabels.length > 0
              ? checkedSingleLabels.join('$')
              : null,
          Description: `Select all applicable answers. If none apply, reply with 'None'. Provide your response as a list of choices separated by a '$' symbol. The available options are: ${Object.keys(map).join(', ')}`,
          Input: (vals) => {
            const want = Array.isArray(vals)
              ? vals
              : String(vals)
                  .split('$')
                  .map((v) => v.trim());
            Object.entries(map).forEach(([lbl, cb]) => {
              const shouldCheck = want.includes(lbl);
              if (shouldCheck && !cb.checked) {
                cb.click();
              }
            });
          }
        }, Object.keys(map));
        return; // stop processing this row
      }
    }
    // The rest of the logic is the same as before
    const radios = isHidden(col2)
      ? null
      : col2?.querySelectorAll('input[type="radio"]');
    if (radios?.length) {
      const radioResult = processRadioGroup(radios);
      let choices = radioResult.choices;
      const visibleRadios = radioResult.visibleRadios;

      // Only add if this radio group is "simple" (no conditional children)
      if (
        hasConditionalChildrenForRadios(col2, radios) &&
        !choices.includes('Other')
      )
        return;

      if (choices.length === 0) choices = ['Yes', 'No'];

      // Skip adding this radio group if "Yes" is already selected
      const yesAlreadySelected = visibleRadios.some((r) => {
        const lbl = (r.parentElement?.innerText?.trim() || r.value || '')
          .trim()
          .toLowerCase();
        return r.checked && lbl === 'yes';
      });
      if (yesAlreadySelected) return; // do not add this group to questions

      const labelOf = (r: HTMLInputElement) =>
        (r.parentElement?.innerText?.trim() || r.value || '').trim();
      const selectedRadio = visibleRadios.find((r) => r.checked);
      section[getUniqueKey(label)] = {
        'Existing Value': selectedRadio ? labelOf(selectedRadio) : null,
        Choices: choices,
        Input: radioResult.Input
      };
      return;
    }

    const select = isHidden(col2) ? null : col2?.querySelector('select');
    if (select && !isHidden(select)) {
      // Treat <select multiple>, size>1, or names ending in [] as multi-selects
      const isMulti =
        select.hasAttribute('multiple') ||
        Number(select.getAttribute('size') || '0') > 1 ||
        /\[\]$/.test(select.name || '');

      if (isMulti) {
        // Build visible choices
        const choices = [...select.options]
          .map((o) => (o.textContent || '').trim())
          .filter(Boolean);

        // Add the multi-select as a flex interaction
        const multiKey = getUniqueKey(label);
        const selectedOpts = [...select.options]
          .filter((o) => o.selected)
          .map((o) => (o.textContent || '').trim());
        section[multiKey] = {
          'Existing Value':
            selectedOpts.length > 0 ? selectedOpts.join('$') : null,
          Choices: choices,
          Input: (vals) => {
            const want = Array.isArray(vals)
              ? vals
              : String(vals)
                  .split('$')
                  .map((v) => v.trim());
            // Additive: answerFlexQuestion calls Input once per '$' item.
            [...select.options].forEach((opt) => {
              const text = (opt.textContent || '').trim();
              if (want.includes(text)) opt.selected = true;
            });
            select.dispatchEvent(new Event('change', { bubbles: true }));
          },
          Clear: () => [...select.options].forEach((opt) => (opt.selected = false))
        };
        markMultiChoice(section[multiKey], choices);

        // Look for an inline or immediate-next-row custom text input (e.g., AggravatingFactorsCust)
        let customInput =
          col2.querySelector(
            select.id ? `input[type="text"]#${select.id}Cust` : ''
          ) ||
          col2.querySelector('input[type="text"][name$="Cust"]') ||
          null;

        if (!customInput) {
          const nextRow = rowsArr[rowIdx + 1];
          if (nextRow && !isHidden(nextRow)) {
            const nextCol1 = nextRow.querySelector('.col1div');
            const nextCol2 = nextRow.querySelector('.col2div');
            const nextLabel = (nextCol1?.innerText || '').trim();
            if (nextCol2 && (!nextLabel || nextLabel === '\u00A0')) {
              customInput = nextCol2.querySelector('input[type="text"]');
            }
          }
        }

        return;
      }

      // Fallback to single-select behavior
      section[getUniqueKey(label)] = createSelectInput(
        select,
        activeTabLabel === 'Objective',
        // Strength/MMT dropdowns should extract grades from combined text;
        // generic Objective custom dropdowns should retain their existing ROM behavior.
        isMmtFieldContext(currentSection, label)
      );
      return;
    }

    if (colLeft || colRight) {
      const leftSelect = colLeft?.querySelector('select');
      // Determine if the row is a PROM (Passive Range of Motion) style row by checking if the label contains '(Right)' or '(Left)'
      const isPROMStyleRow =
        label.includes('(Right)') || label.includes('(Left)');
      // Set the suffix for the left column key: use 'Endfeel' for PROM-style rows, otherwise use 'Left'
      const leftColSuffix = isPROMStyleRow ? 'Endfeel' : 'Left';
      if (leftSelect && !isHidden(leftSelect)) {
        // Add left select mapping: if PROM-style, use Endfeel suffix; otherwise, use Left suffix
        section[getUniqueKey(`${label} - ${leftColSuffix}`)] =
          createSelectInput(
            leftSelect,
            activeTabLabel === 'Objective',
            // Left/right Strength rows share ROM-like labels such as Flexion/Rotation,
            // so pass the section context explicitly before enabling MMT extraction.
            isMmtFieldContext(currentSection, label, leftColSuffix)
          );
      }
      const rightSelect = colRight?.querySelector('select');
      if (rightSelect && !isHidden(rightSelect)) {
        section[getUniqueKey(`${label} - Right`)] = createSelectInput(
          rightSelect,
          activeTabLabel === 'Objective',
          // Right-side Strength rows need the same context gate as left-side rows.
          isMmtFieldContext(currentSection, label, 'Right')
        );
      }
      if (leftSelect || rightSelect) return;
    }

    const textInput = isHidden(col2)
      ? null
      : col2?.querySelector('input[type="text"]');
    if (textInput && !isHidden(textInput)) {
      section[getUniqueKey(label)] = {
        'Existing Value': getExistingNote(
          (textInput as HTMLInputElement).value
        ),
        Input: (val, replyType = 'overwrite') =>
          handleTextInput(textInput, val, replyType)
      };
      return;
    }

    const textarea = isHidden(col2) ? null : col2?.querySelector('textarea');
    if (textarea && !isHidden(textarea)) {
      section[getUniqueKey(label)] = {
        'Existing Value': getExistingNote(
          (textarea as HTMLTextAreaElement).value
        ),
        Input: (val, replyType = 'overwrite') =>
          handleTextInput(textarea, val, replyType)
      };
      return;
    }

    const table = isHidden(col2) ? null : col2?.querySelector('table');
    const checkRows =
      table && !isHidden(table) ? table.querySelectorAll('tr') : null;
    if (checkRows?.length) {
      const map = extractCheckboxMapFromTable(table);
      section[getUniqueKey(label)] = createCheckboxInput(map);
      return;
    }

    if (colLeft?.querySelector('table') && !isHidden(colLeft)) {
      const t = colLeft.querySelector('table');
      if (!isHidden(t)) {
        const map = extractCheckboxMapFromTable(t);
        section[getUniqueKey(`${label} - Left`)] = createCheckboxInput(map);
      }
    }

    if (colRight?.querySelector('table') && !isHidden(colRight)) {
      const t = colRight.querySelector('table');
      if (!isHidden(t)) {
        const map = extractCheckboxMapFromTable(t);
        section[getUniqueKey(`${label} - Right`)] = createCheckboxInput(map);
      }
    }

    // Nested checkbox groups (skip if hidden)
    if (
      row.matches('.rowdiv.nopadding') &&
      !row.classList.contains('hidden') &&
      row.querySelector('input[type="checkbox"]') &&
      row.nextElementSibling?.classList.contains('indented') &&
      !row.nextElementSibling?.classList.contains('hidden')
    ) {
      const parentLabel = row.innerText.trim();
      const childBlock = row.nextElementSibling;
      if (isHidden(childBlock)) return;

      const nestedCheckboxes = childBlock.querySelectorAll(
        'input[type="checkbox"]'
      );
      const checkboxMap = {};
      nestedCheckboxes.forEach((cb) => {
        if (isHidden(cb)) return;
        const lbl = cb.parentElement?.innerText?.trim();
        if (cb && lbl) checkboxMap[lbl] = cb;
      });

      const nestedTextarea = childBlock.querySelector('textarea');
      section[getUniqueKey(parentLabel)] = createCheckboxInput(checkboxMap);

      if (nestedTextarea && !isHidden(nestedTextarea)) {
        section[getUniqueKey(`${parentLabel} Notes`)] = {
          'Existing Value': getExistingNote(
            (nestedTextarea as HTMLTextAreaElement).value
          ),
          Input: (val, replyType = 'overwrite') =>
            handleTextInput(nestedTextarea, val, replyType)
        };
      }

      return;
    }

    // Inset tests (ignore hidden variant)
    if (row.classList.contains('inset-tests')) {
      if (row.classList.contains('hidden') || isHidden(row)) return;

      const prevLabel =
        row.previousElementSibling
          ?.querySelector('.col1div')
          ?.innerText?.trim() || 'Untitled';
      const groupLabel =
        row.previousElementSibling?.previousElementSibling
          ?.querySelector('.col1div')
          ?.innerText?.trim() || '';
      const combinedKey = groupLabel
        ? `${groupLabel} - ${prevLabel}`
        : prevLabel;
      const key = getUniqueKey(combinedKey);

      const multi = row.querySelector('select[multiple]');
      if (multi && !isHidden(multi)) {
        let choices = [...multi.options].map((o) => o.textContent.trim());
        if (choices.length === 0) choices = ['Yes', 'No'];
        const selectedMulti = [...multi.options]
          .filter((o) => o.selected)
          .map((o) => o.textContent.trim());
        section[key] = {
          'Existing Value':
            selectedMulti.length > 0 ? selectedMulti.join('$') : null,
          Choices: choices,
          Input: (vals) => {
            // Additive: answerFlexQuestion calls Input once per '$' item.
            [...multi.options].forEach((opt) => {
              if (vals.includes(opt.textContent.trim())) opt.selected = true;
            });
            multi.dispatchEvent(new Event('change', { bubbles: true }));
          },
          Clear: () => [...multi.options].forEach((opt) => (opt.selected = false))
        };
        markMultiChoice(section[key], choices);
      }

      const textInput2 = row.querySelector('input[type="text"]');
      if (textInput2 && !isHidden(textInput2)) {
        section[key] = {
          'Existing Value': getExistingNote(
            (textInput2 as HTMLInputElement).value
          ),
          Input: (val, replyType = 'overwrite') =>
            handleTextInput(textInput2, val, replyType)
        };
      }
    }
  });

  Object.values(structure).forEach((sectionObj) => {
    const keys = Object.keys(sectionObj);

    keys.forEach((key) => {
      keys.forEach((otherKey) => {
        if (key !== otherKey) {
          const normKey = key.replace(/[:\s]+$/, '');
          const normOther = otherKey.replace(/[:\s]+$/, '');

          // If the longer key contains the shorter key, delete the shorter one
          if (
            normOther.length > normKey.length &&
            normOther.endsWith(normKey)
          ) {
            delete sectionObj[key];
          }
        }
      });
    });
  });

  // --- Split top-level sections into separate Flex questions ---
  // The top-level keys in `structure` (e.g., "Basic Info", "Medical History", "Pain", etc.)
  (function splitTopLevelSectionsIntoQuestions() {
    if (!structure || typeof structure !== 'object') return;

    // Remove the aggregate parent question for this tab (e.g., "Subjective") to avoid duplication.
    // We'll emit one Flex question per top-level section instead.
    if (
      questionInteractions[key] &&
      (key === 'Subjective' || key === 'Objective')
    ) {
      delete questionInteractions[key];
    } else {
      return;
    }

    Object.entries(structure).forEach(([sectionName, sectionObj]) => {
      if (!sectionName || typeof sectionObj !== 'object') return;

      // Normalize section name for matching header text (remove colons/trim)
      const normalizedName = sectionName.replace(/[:\s]+$/g, '').trim();
      sectionName = normalizedName;

      sectionName =
        key === 'Objective' && isWebPTUser
          ? normalizedName + ' - Flex'
          : sectionName;
      if (
        !questionInteractions[sectionName] &&
        sectionName !== 'Current Functional Limitations'
      ) {
        questionInteractions[sectionName] = {
          Key: sectionName,
          Question: sectionName,
          Type: 'Flex',
          Structure: { [sectionName]: sectionObj }
        };
      }
    });
  })();
  // Ensure dense radio (pain-scale) blocks are added under the "Pain" section only
  // Try to find an existing top-level section that normalizes to "pain"
  const painKey =
    Object.keys(structure).find(
      (k) =>
        k &&
        k
          .replace(/[:\s]+$/g, '')
          .trim()
          .toLowerCase() === 'pain'
    ) || 'Pain';

  // Make sure the Pain section exists
  if (!structure[painKey] || typeof structure[painKey] !== 'object') {
    structure[painKey] = {};
  }

  // Populate only the Pain section with dense radio blocks (Pain Scale 1, 2, ...)
  processDenseRadioBlocks(tabContent, structure[painKey]); // goes under Pain only
}

function extractAssessmentGoals() {
  const activeTabLabel = document
    .querySelector('.zpTabsActive')
    ?.textContent?.trim();
  if (['Subjective', 'Objective', 'Plan', 'Billing'].includes(activeTabLabel))
    return;

  const tabContent = document.querySelector(`.zpTab#${activeTabLabel}`);
  if (!tabContent) return;

  const container = document.querySelector('#container_goals');
  if (!container) return;

  initializeGoalsInteraction();

  // reset structure in Python-style format (array of objects)
  questionInteractions['Goal List'].Structure = [];

  const goalEntries = Array.from(
    container.querySelectorAll('[name^="goal_"]')
  ).filter((goal) => goal.style.display !== 'none');

  goalEntries.forEach((goal, idx) => {
    const descEl = goal.querySelector(`#GoalDesc${idx}`);
    const typeEl = goal.querySelector(
      `#GoalType${idx}`
    ) as HTMLSelectElement | null;
    const durationEl = goal.querySelector(
      `#GoalDuration${idx}`
    ) as HTMLSelectElement | null;
    const metEl = goal.querySelector(
      `#GoalMetPercentage${idx}`
    ) as HTMLSelectElement | null;
    const progressEl = goal.querySelector(
      `#GoalProgress${idx}`
    ) as HTMLSelectElement | null;

    // Use lazy getters for Choices so they are read from the live DOM at
    // serialisation time, not at structure-build time when selects may still be empty.
    const termBase = createDropdownInput(typeEl);
    const lengthBase = createDropdownInput(durationEl);
    const metBase = createDropdownInput(metEl);

    const lazyChoices = (
      base: ReturnType<typeof createDropdownInput>,
      el: HTMLSelectElement | null
    ) =>
      Object.defineProperty({ ...base }, 'Choices', {
        enumerable: true,
        get: () => {
          const live = Array.from(el?.options ?? [])
            .map((o) => o.textContent)
            .filter(Boolean);
          return live.length > 0 ? live : base.Choices;
        }
      });

    const goalObj = {
      'Goal Description': createTextInput(descEl),
      Term: lazyChoices(termBase, typeEl),
      Length: lazyChoices(lengthBase, durationEl),
      'Goal Met': lazyChoices(metBase, metEl),
      Progress: createTextInput(progressEl)
    };

    questionInteractions['Goal List'].Structure.push(goalObj);
  });
}

function initializeGoalsInteraction() {
  if (!questionInteractions['Goal List']) {
    questionInteractions['Goal List'] = {
      Key: 'Goal List',
      Question: 'Goal List',
      Type: 'Flex',
      Structure: {},
      Elements: []
    };
  }
}

function createTextInput(el) {
  return {
    'Existing Value': el?.value || '',
    Input: (val, replyType = 'overwrite') => {
      if (replyType === 'overwrite') {
        el.value = val;
        el.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        el.value = el.value + val;
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };
}

function createDropdownInput(selectEl) {
  const selectedText =
    (selectEl?.options[selectEl?.selectedIndex]?.textContent || '').trim() ||
    null;
  return {
    'Existing Value': selectedText,
    Choices: [...(selectEl?.options || [])].map((opt) => opt.textContent),
    Input: (val) => {
      for (let opt of selectEl?.options || []) {
        if (opt.textContent === val) {
          selectEl.value = opt.value;
          selectEl.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }
  };
}

function isEmptyOrNullorSameAsExisting(aiResponse, existingValue) {
  return (
    aiResponse === null ||
    aiResponse === undefined ||
    aiResponse === '' ||
    aiResponse === 'None' ||
    aiResponse === existingValue
  );
}

function getExistingNote(value) {
  if (typeof value !== 'string') {
    return null;
  }

  let val = value?.trim();
  if (val === '') return null;
  if (val.startsWith('<?xml')) return null;
  return val;
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]
 
*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/
var questionInteractions = {};

// NewWebPTInject scrapes every tab through getQuestions, then installs the merged map.
export function getQuestionInteractions() {
  return questionInteractions;
}

export function setQuestionInteractions(map) {
  questionInteractions = map;
}

var header = '';

export function getQuestions(isWebPTUser = false) {
  // function getQuestions() {
  if (isWebPTUser) lastKnownIsWebPTUser = true;
  questions = [];
  questionInteractions = {};
  const noteTypeElement = document.querySelector('#noteTitle');
  let noteType = noteTypeElement?.textContent.trim() || '';
  // Remove "You are currently documenting... text " onward
  noteType = noteType.replace(/You are.*/i, '').trim();

  var qs = getWebPTQuestions(null, isWebPTUser, noteType);
  if (qs && Object.keys(qs).length > 0) {
    for (const [key, value] of Object.entries(qs)) {
      questionInteractions[key] = value;
    }
  }
  extractFlexQuestions(isWebPTUser, noteType);
  extractAssessmentGoals();

  for (const [key, value] of Object.entries(questionInteractions)) {
    if (key.includes('Untitled')) {
      continue;
    }

    questions.push({
      Question: key,
      Key: value['Key'],
      Type: value['Type'],
      Choices: value['Options'] || value['Choices'],
      ExistingNote: value['ExistingNote'] || null,
      Structure: value['Structure'] || {},
      ReplyType: key.includes(' - Flex') && isWebPTUser ? 'no_reply' : null
    });
  }
  console.log('Questions');
  console.log(questions);
  return questions;
}

/**
 * Return current note values keyed by question key. Long answers are strings;
 * Flex values preserve their nested section and field keys.
 *
 * { [questionKey]: string | { [fieldKey]: string | nested values } }
 *
 * WebPT 1.0 splits a note across tabs and only the active tab is scrapeable, so
 * each call returns the active tab. The note store merges snapshots, so the
 * full note accumulates as the user moves through tabs.
 */
export function getQuestionsForCapture(): Record<string, any> {
  // getQuestions rebuilds the module-level questions/questionInteractions, and
  // the panel's answer path resolves its Input closures out of the latter.
  // Restore both so a capture poll can never disturb an in-flight Comprehend.
  // This is airtight because the whole scrape is synchronous: nothing else can
  // run between the save and the restore, and getQuestions replaces (never
  // mutates) the saved objects.
  const savedQuestions = questions;
  const savedInteractions = questionInteractions;
  captureScrapeActive = true;
  try {
    return extractCaptureValues(getQuestions(lastKnownIsWebPTUser));
  } finally {
    captureScrapeActive = false;
    questions = savedQuestions;
    questionInteractions = savedInteractions;
  }
}

function extractCaptureValues(questions) {
  const values = {};

  questions.forEach((question, index) => {
    const key = question?.Key || question?.Question || String(index);
    const value = getCaptureValue(question);
    if (value !== null && value !== undefined) {
      values[key] = value;
    }
  });

  return values;
}

function getCaptureValue(question) {
  if (question.ExistingNote !== null && question.ExistingNote !== undefined) {
    return question.ExistingNote;
  }
  if (
    question['Existing Value'] !== null &&
    question['Existing Value'] !== undefined
  ) {
    return question['Existing Value'];
  }
  if (question.Structure) {
    const values = extractFlexCaptureValues(question.Structure);
    return Object.keys(values).length > 0 ? values : null;
  }
  return null;
}

function extractFlexCaptureValues(structure) {
  const values = {};

  Object.entries(structure || {}).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') return;

    if (Object.prototype.hasOwnProperty.call(value, 'Existing Value')) {
      values[key] = value['Existing Value'] ?? '';
      return;
    }

    const nested = extractFlexCaptureValues(value);
    if (Object.keys(nested).length > 0) {
      values[key] = nested;
    }
  });

  return values;
}

/**
 * Return a stable key for the open note. Each EMR may derive this differently,
 * but the same note must always return the same key and two notes must not
 * share one. WebPT 1.0 serves the note editor from addSOAP.php with the patient
 * and appointment ids in the query string.
 */
export function getNoteKeyForCapture() {
  if (!/addsoap\.php/i.test(window.location.pathname)) return null;

  const params = new URLSearchParams(window.location.search);
  const patientId = params.get('ID');
  const aptId = params.get('myAptID');
  if (!patientId || !aptId) return null;
  return `patient:${patientId}|apt:${aptId}`;
}

/*
The Finalize button:

  <input type="button" class="link ta--soap--finalize"
         onclick="SoapObj.SaveForm('PFB2','finalize','Finalize Document')">

Best-effort, not proof. Our listener is capture-phase so it runs before WebPT's
onclick, and SaveForm is abortable: it defaults to SoapSkipConfirmation = false
and gates on a native confirm(), calling cancelAlert()/__CancelSave() if the user
declines, plus a hard block when `preventNoteFinalization && isBilled()`. A
declined confirm therefore still marks the note signed off here.

That's tolerable because sign-off no longer closes a note to capture — the next
poll corrects a premature mark, and the backend upserts. Forward is deliberately
excluded: a note awaiting co-signature isn't signed.
*/
export const noteFinalizationSelectorForCapture = 'input.ta--soap--finalize';

export function highlightFields(container = null) {
  // If no container provided, search the entire document for radio buttons
  const searchContainer = container || document;

  const radios = searchContainer.querySelectorAll('input[type="radio"]');
  const highlighted = [];

  // Add visual highlight
  radios.forEach((radio) => {
    radio.style.outline = '2px solid red';
    radio.style.outlineOffset = '2px';

    const label = radio.closest('label') || radio.parentElement;
    if (label && !label.innerText.includes('Complexity')) {
      label.style.backgroundColor = '#6bd888';
      highlighted.push(label);
      highlighted.push(radio);
    }
  });

  // Remove highlight + message after 60s
  setTimeout(() => {
    highlighted.forEach((el) => {
      if (!el) return;
      el.style.outline = '';
      el.style.outlineOffset = '';
      el.style.backgroundColor = '';
    });
  }, 60000);
}

export function answerQuestions(answers) {
  // function answerQuestions(answers) {
  /*
          Description: This function will take the answers and populate the questions
          
          Args: 
              answers: A dictionary of answers
                  "Question": "The question in question"
                  "Answer": "The answer to the question"
         
          Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
              [
                  {
                      Question: "Where was the injury?", 
                      Answer: "Back" 
                      Error: "The error message" (Choice not found, etc)
                  },
                  ...
              }
      
          */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  /*
          Description: This function will take the answer and populate the question
          
          Args: 
              question: The question in question
              answer: The answer to the question
         
          Return: null if success otherwise a string with the error message
          */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

async function answerFlexQuestion(question, answer, replyType = 'overwrite') {
  try {
    const errors = [];
    const questionObject = questionInteractions[question];

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    console.log('Question found:', questionObject);

    // --- Ensure enough goal boxes exist if answer is an array ---
    if (Array.isArray(answer)) {
      const container = document.querySelector('#container_goals');
      if (container) {
        let existingGoals = Array.from(
          container.querySelectorAll('[name^="goal_"]')
        ).filter((goal) => goal.style.display !== 'none'); //only visible

        while (existingGoals.length < answer.length) {
          // 1. Check if there's a hidden goal we can reuse
          const hiddenGoal = Array.from(
            container.querySelectorAll('[name^="goal_"]')
          ).find((goal) => goal.style.display === 'none');

          if (hiddenGoal) {
            hiddenGoal.style.display = ''; // or "block"
          } else {
            // 2. Otherwise, fill lastDesc and click Add Another Goal
            const lastIdx = existingGoals.length - 1;
            const lastDesc = document.querySelector(`#GoalDesc${lastIdx}`);
            if (lastDesc && !lastDesc.value.trim()) {
              lastDesc.value = ' ';
              lastDesc.dispatchEvent(new Event('input', { bubbles: true }));
            }

            const addLink = document.querySelector(
              'a.link[onclick^="goalAdd"]'
            );
            if (addLink) {
              addLink.click();
            }
          }

          // Refresh only visible goals
          existingGoals = Array.from(
            container.querySelectorAll('[name^="goal_"]')
          ).filter((goal) => goal.style.display !== 'none');
        }

        // 🔑 Rebuild structure after new boxes are available
        extractAssessmentGoals();
        console.log('Structure refreshed after adding/reusing goals');
      }
    }
    // ------------------------------------------------------------
    const structure = questionObject.Structure || {};

    // --- Handle array-style answers (Goal List) ---
    if (Array.isArray(answer.answer)) {
      for (let i = 0; i < answer.answer.length; i++) {
        const field = structure[i];
        if (!field) {
          console.error(`No structure found for index ${i}`);
          errors.push({ Field: i, Error: 'Field not found in Structure' });
          continue;
        }

        const fieldDetails = answer.answer[i];
        for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
          const valuesToInput =
            typeof value === 'string'
              ? value.split('$').map((v) => v.trim())
              : [value];

          for (const val of valuesToInput) {
            if (field[subFieldKey]?.Input) {
              try {
                await field[subFieldKey].Input(val, replyType);
              } catch (error) {
                errors.push({
                  Field: `Goal ${i + 1}.${subFieldKey}`,
                  Error: `Error applying value: ${error.message}`
                });
              }
            } else {
              errors.push({
                Field: `Goal ${i + 1}.${subFieldKey}`,
                Error: 'Input function not found'
              });
            }
          }
        }
      }
    } else {
      // --- Handle object-style answers (old behavior)---
      for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
        const field = structure[fieldKey];

        if (!field) {
          console.error(`Field "${fieldKey}" not found in the Structure.`);
          errors.push({
            Field: fieldKey,
            Error: 'Field not found in Structure'
          });
          continue;
        }

        // ROM: if API sent "Custom" + numbers in Additional Comments, replace Custom with parsed degree so Input("151") runs
        const acKey = Object.keys(field || {}).find(
          (k) =>
            typeof k === 'string' &&
            k.includes('Additional Comments') &&
            field[k]?.Input &&
            !field[k]?.Choices
        );
        const acText = acKey ? fieldDetails[acKey] : null;
        const parsed =
          typeof acText === 'string' && acText.length > 40
            ? parseROMComments(acText)
            : null;
        if (parsed) {
          for (const [subKey, _v] of Object.entries(fieldDetails)) {
            const choices = field[subKey]?.Choices || [];
            if (!choices.includes('Custom')) continue;
            const n = normROMKey(subKey);
            const val = getParsedROM(parsed, n);
            if (val != null) fieldDetails[subKey] = val;
          }
        }

        const isNumericCustomField = (key) =>
          /AROM|PROM|Flexion|Extension|Abduction|Rotation|Bending|Reach|Scaption|\(Custom\)|Forward Bending|Backward Bending|Side Bending/i.test(
            key
          );

        const isMmtField = (subFieldKey) =>
          isMmtFieldContext(fieldKey, subFieldKey);
        // Collect all comments extracted from numeric ROM fields so we set Additional Comments once.
        // Strength measurements can share labels like Flexion/Rotation; preserve their Custom text.
        const extractedComments = [];
        for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
          const valuesToInput =
            typeof value === 'string'
              ? value.split('$').map((v) => v.trim())
              : [value];
          for (const val of valuesToInput) {
            if (
              typeof val === 'string' &&
              acKey &&
              isNumericCustomField(subFieldKey) &&
              !isMmtField(subFieldKey)
            ) {
              const { numericPart, commentPart } = parseNumericAndComment(val);
              const romComment = buildROMComment(
                fieldKey,
                subFieldKey,
                numericPart,
                commentPart
              );
              if (romComment) extractedComments.push(romComment);
            }
          }
        }
        const acValue =
          Array.from(
            new Set([
              ...(acText ? [String(acText).trim()] : []),
              ...extractedComments
            ])
          )
            .filter(Boolean)
            .join('\n') || undefined;

        for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
          // Handle comma-separated values for checkbox inputs
          const valuesToInput =
            typeof value === 'string'
              ? value.split('$').map((v) => v.trim())
              : [value];
          // Multi-select Inputs are additive, so overwrite clears old picks first.
          if (
            replyType !== 'append' &&
            field[subFieldKey]?.Clear &&
            valuesToInput.some((v) => v !== '' && v != null)
          ) {
            field[subFieldKey].Clear();
          }

          for (const val of valuesToInput) {
            if (field[subFieldKey]?.Input) {
              try {
                let valueToInput = val;
                const mmtFieldHere = isMmtField(subFieldKey);
                const mmtGrade = mmtFieldHere ? extractMmtGrade(val) : null;
                if (mmtGrade) {
                  valueToInput = mmtGrade;
                } else if (
                  !mmtFieldHere &&
                  typeof val === 'string' &&
                  acKey &&
                  isNumericCustomField(subFieldKey)
                ) {
                  const { numericPart, commentPart } =
                    parseNumericAndComment(val);
                  if (numericPart != null && commentPart) {
                    valueToInput = numericPart;
                  }
                }
                if (subFieldKey === acKey) {
                  await field[acKey].Input(
                    acValue !== undefined ? acValue : valueToInput,
                    replyType
                  );
                } else {
                  await field[subFieldKey].Input(valueToInput, replyType);
                }
              } catch (error) {
                errors.push({
                  Field: `${fieldKey}.${subFieldKey}`,
                  Error: `Error applying value: ${error.message}`
                });
              }
            } else {
              console.warn(
                `Input function not found for "${fieldKey}.${subFieldKey}"`
              );
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: 'Input function not found'
              });
            }
          }
        }

        if (
          acKey &&
          acValue !== undefined &&
          !Object.prototype.hasOwnProperty.call(fieldDetails, acKey)
        ) {
          try {
            await field[acKey].Input(acValue, replyType);
          } catch (error) {
            errors.push({
              Field: `${fieldKey}.${acKey}`,
              Error: `Error applying value: ${error.message}`
            });
          }
        }      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  }
}
// getQuestions()

export async function getLogFromInjector() {
  // Scrape errors are reported by shipping the whole page. Capture polls in the
  // background, so a recurring error would upload the note every few seconds.
  if (captureScrapeActive) return null;

  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

function normROMKey(key) {
  if (!key || typeof key !== 'string') return '';
  return key
    .replace(/\b(AROM|PROM)\s*/gi, '')
    .replace(/\s*-\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Resolve parsed value with fallbacks so "functional external rotation reach left" matches "shoulder functional external rotation reach left" and vice versa
function getParsedROM(parsed, normalizedKey) {
  if (parsed[normalizedKey] != null) return parsed[normalizedKey];
  if (normalizedKey.startsWith('shoulder '))
    return parsed[normalizedKey.replace(/^shoulder\s+/, '')] ?? null;
  return parsed['shoulder ' + normalizedKey] ?? null;
}

// Parse "• Shoulder flexion: Left 138°, Right 151°" or "Left 138 degrees; Right 151 degrees" into { "shoulder flexion left": "138", ... }
function parseROMComments(text) {
  const out = {};
  if (!text || typeof text !== 'string') return out;
  for (const line of text.split(/\r?\n/)) {
    const m = line.match(/^\s*[•\-]?\s*(.+?):\s*(.+)$/);
    if (!m) continue;
    const label = m[1]
      .trim()
      .toLowerCase()
      .replace(/\s*-\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const rest = m[2].trim();
    // Left 138 degrees; Right 151 degrees | Left 138°, Right 151°
    const lr = rest.match(
      /Left\s+(\d+)\s*(?:°|\s*degrees?)?\s*[,;]\s*Right\s+(\d+)\s*(?:°|\s*degrees?)?/i
    );
    if (lr) {
      out[label + ' left'] = lr[1];
      out[label + ' right'] = lr[2];
    } else {
      const one = rest.match(/^(\d+)\s*°?(?:\s*degrees?)?\s*$/i);
      if (one) {
        out[label] = one[1];
        if (label.startsWith('scapula ')) {
          const short = label.replace(/^scapula\s+/, '');
          if (short) out[short] = one[1];
        }
      }
    }
  }
  return out;
}

// Helper to sanitize the question key
function sanitizeQuestionKey(text) {
  if (!text || typeof text !== 'string') return text;

  return (
    text
      // Take only the first line of the label text
      .trim()
      .split('\n')[0]
      .trim()
      // Remove required-field helper text (with or without newlines)
      .replace(/\s*\*?\s*This is a required field\s*/gi, '')
      // Remove newlines entirely
      .replace(/[\r\n]+/g, ' ')
      // Collapse extra spaces
      .replace(/\s{2,}/g, ' ')
      // Remove trailing colons
      .replace(/:\s*$/, '')
      .trim()
  );
}
